// frontend/src/pages/admin/AnalyseCspe.jsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useOutletContext, useParams, useLocation, Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import {
    FiFileText, FiCheckSquare, FiXSquare, FiArrowLeft,
    FiDownload, FiAlertTriangle, FiClock, FiChevronDown, FiChevronUp,
    FiEye, FiX, FiLoader, FiCheck, FiDownloadCloud, FiLayers, 
    FiSend, FiEdit3, FiUser, FiBarChart2, FiTarget,
    FiRefreshCw, FiUpload, FiCpu, FiCheckCircle, FiPenTool, FiTrash2, FiSearch, FiFilter, FiSettings, FiShield
} from 'react-icons/fi';
import { FaEuroSign } from 'react-icons/fa';
import axios from 'axios';
import toast from 'react-hot-toast';
import { identifierFournisseursSansBloquer, avertissementNonIdentifiees } from '../../utils/identificationFournisseurs';
import DocumentActionButtons from '../../components/cspe/DocumentActionButtons';
import CerfaNotificationSection from '../../components/cspe/CerfaNotificationSection';  
import AssimilationTabContent from '../../components/cspe/AssimilationTabContent';
import { buildStoragePayload } from '../../utils/storagePayload';
import { computeUploadPercent, formatFileSize, uploadErrorMessage } from '../../utils/uploadHelpers';
import TicfeInvoiceEditorModal from '../../components/cspe/TicfeInvoiceEditorModal';
import PdlSiretEditorModal from '../../components/cspe/PdlSiretEditorModal';
import PdlExonerationEditorModal from '../../components/cspe/PdlExonerationEditorModal';
import RegenerationVerrouilleeModal from '../../components/cspe/RegenerationVerrouilleeModal';
import { peutGenererEra, peutGenerer3310, libelleExoneration, tousPdlsExoneres, afficherBandeauPrerequisEra, documentsAFusionner, estPdlExonere } from '../../utils/cspeGating';
import PiecesDossierExoneration from '../../components/cspe/PiecesDossierExoneration';
import AccountRaisonSocialeEditorModal from '../../components/cspe/AccountRaisonSocialeEditorModal';
import SignedContractsCSPE from '../../components/cspe/SignedContractsCSPE';
import { confirmToast } from '../../lib/confirmToast';

// ===== SYSTÈME D'ONGLETS COMPACT =====
const TabSystem = ({ activeTab, onTabChange }) => {
  const tabs = [
    { id: 'analyse', label: 'Analyse CSPE', icon: FiBarChart2 },
    { id: 'assimilation', label: 'Assimilation', icon: FiTarget }
  ];

  return (
    <div className="border-b border-gray-200 bg-white">
      <nav className="flex space-x-2 px-2" aria-label="Tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            className={`${
              activeTab === tab.id
                ? 'border-orange-500 text-orange-600 bg-orange-50'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } group inline-flex items-center py-1.5 px-2 border-b-2 font-semibold text-xs transition-all duration-200 rounded-t`}
          >
            <tab.icon
              className={`${
                activeTab === tab.id ? 'text-orange-500' : 'text-gray-400 group-hover:text-gray-500'
              } -ml-0.5 mr-1 h-3 w-3`}
            />
            {tab.label}
          </button>
        ))}
      </nav>
    </div>
  );
};

// ===== HEADER ULTRA COMPACT =====
const CompactHeader = ({ client, loading, onRefresh, selectedYear, onEditRaisonSociale }) => {
  return (
    <div className="bg-gradient-to-r from-orange-600 to-red-700 text-white px-3 py-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center space-x-2 min-w-0">
          <Link
            to="/dashboard/admin/analyse-cspe"
            className="p-1 hover:bg-white/10 rounded transition-colors"
            title="Retour"
          >
            <FiArrowLeft className="h-4 w-4" />
          </Link>

          <div className="min-w-0">
            <h1 className="text-sm font-semibold truncate flex items-center gap-1">
              <span>Analyse CSPE - {client?.company_name || client?.full_name || 'Client'}</span>
              {(client?.account_id || client?.id) && onEditRaisonSociale && (
                <button
                  onClick={onEditRaisonSociale}
                  className="p-0.5 rounded hover:bg-white/20 transition-colors"
                  title="Modifier la raison sociale (apparaît sur les CERFA et contrat)"
                  aria-label="Modifier la raison sociale"
                >
                  <FiEdit3 className="h-3 w-3" />
                </button>
              )}
            </h1>

            <div className="flex items-center flex-wrap gap-x-2 gap-y-1 text-[10px] opacity-90 mt-0.5">
              <span className="bg-white/10 px-1.5 py-0.5 rounded">
                Account ID: {client?.account_id}
              </span>
              <span className="opacity-75">•</span>
              <span className="opacity-90">Taxes énergétiques</span>
              {selectedYear && (
                <>
                  <span className="opacity-75">•</span>
                  <span className="opacity-90">{selectedYear}</span>
                </>
              )}
            </div>
          </div>
        </div>

        <button
          onClick={onRefresh}
          disabled={loading}
          className="p-1.5 hover:bg-white/10 rounded transition-colors disabled:opacity-50"
          title="Actualiser"
        >
          <FiRefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>
    </div>
  );
};

// ===== DÉFINITIONS ET CONSTANTES (inchangées) =====
const DOCUMENT_CATEGORIES = {
  'documents_financiers': "Documents financiers (Liasse fiscale)",
};

const DOCUMENT_CONFIG = [
  { 
    key: 'documents_financiers', 
    name: "Documents financiers (Liasse fiscale)", 
    optional: false 
  },
];

const STATUS_DISPLAY = {
  'uploaded': { text: 'Reçu', color: 'bg-blue-100 text-blue-800', icon: <FiFileText /> },
  'En attente': { text: 'En attente', color: 'bg-yellow-100 text-yellow-800', icon: <FiClock /> },
  'En attente de validation': { text: 'En attente', color: 'bg-yellow-100 text-yellow-800', icon: <FiClock /> },
  'En attente de validation manuelle': { text: 'Validation manuelle requise', color: 'bg-orange-100 text-orange-800', icon: <FiEdit3 /> },
  'Validé': { text: 'Validé', color: 'bg-green-100 text-green-800', icon: <FiCheckSquare /> },
  'Validé manuellement': { text: 'Validé manuellement', color: 'bg-emerald-100 text-emerald-800', icon: <FiCheckSquare /> },
  'Rejeté': { text: 'Rejeté', color: 'bg-red-100 text-red-800', icon: <FiAlertTriangle /> },
  'Rejeté par admin': { text: 'Rejeté par admin', color: 'bg-red-100 text-red-800', icon: <FiAlertTriangle /> },
  'À fournir': { text: 'À fournir', color: 'bg-slate-200 text-slate-700', icon: <FiFileText /> },
};

const SUPABASE_DEFAULT_BUCKET = 'user-documents';
const BUCKET_NAME = 'generated-cerfas';
const SIGNED_CERFA_BUNDLES_BUCKET = 'cerfa-bundles';

// ===== FONCTIONS UTILITAIRES (inchangées) =====
const buildStoragePath = (doc) => {
  if (doc?.s3_key && doc?.s3_bucket) return `s3://${doc.s3_bucket}/${doc.s3_key}`;
  if (doc?.old_supabase_storage_path) {
    const s = doc.old_supabase_storage_path;
    if (s.startsWith('supabase://') || s.startsWith('s3://')) return s;
    return `supabase://user-documents/${s}`;
  }
  throw new Error("Emplacement du fichier introuvable (S3 ou Supabase).");
};

const getCategoryStatus = (documentsInCategory) => {
  if (!documentsInCategory || documentsInCategory.length === 0) {
    return { text: 'À fournir', color: 'bg-slate-200 text-slate-700' };
  }
  const allValidated = documentsInCategory.every(doc => doc.status === 'Validé');
  if (allValidated) {
    return { text: 'Complet', color: 'bg-green-100 text-green-800' };
  }
  const someRejected = documentsInCategory.some(doc => doc.status === 'Rejeté');
  if (someRejected) {
    return { text: 'Action Requise', color: 'bg-red-100 text-red-800' };
  }
  const somePending = documentsInCategory.some(doc => 
    doc.status === 'En attente' || doc.status === 'En attente de validation'
  );
  if (somePending) {
    return { text: 'En attente', color: 'bg-yellow-100 text-yellow-800' };
  }
  const someProvidedButNotFinal = documentsInCategory.some(doc => 
    doc.status && doc.status !== 'À fournir'
  );
  if (someProvidedButNotFinal) {
      return { text: 'Partiel', color: 'bg-yellow-100 text-yellow-700' };
  }
  return { text: 'À fournir', color: 'bg-slate-200 text-slate-700' };
};

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'document.pdf';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ===== COMPOSANT SECTION DOCUMENT COMPACT =====
const DocumentCategorySection = ({
  categoryKey,
  categoryName,
  isOptional, 
  documents,
  onValidate,
  onReject,
  onProcessLiasse,  
  onValidateAllInCategory,
  children,
}) => {
  const [isOpen, setIsOpen] = useState(true);

  const docsNeedOcr = documents.filter(doc => 
    doc.document_type_id === 'documents_financiers' && !doc.ocr_done
  );
  const hasDocsToOcr = docsNeedOcr.length > 0;

  const documentsEnAttenteDeValidation = documents.filter(doc => 
    doc.status === 'En attente' || doc.status === 'En attente de validation'
  );
  const canValidateAll = documentsEnAttenteDeValidation.length > 0;
  const categoryStatus = getCategoryStatus(documents);

  return (
    <section key={categoryKey} className="bg-white rounded-md shadow-sm border border-gray-200">
      <div 
        className="flex justify-between items-center p-3 hover:bg-slate-50/70 rounded-t-md cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setIsOpen(!isOpen); }}
        aria-expanded={isOpen}
        aria-controls={`category-content-${categoryKey}`}
      >
        <div className="flex items-center">
          <h3 className="text-sm font-medium text-slate-800">{categoryName}</h3>
          {isOptional && (
            <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-sky-100 text-sky-800">
              Optionnel
            </span>
          )}
          <span 
            className={`ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${categoryStatus.color}`}
            title={`Statut de la catégorie: ${categoryStatus.text}`}
          >
            {categoryStatus.icon && React.cloneElement(categoryStatus.icon, { className: "h-3 w-3 mr-1"})}
            {categoryStatus.text}
          </span>
        </div>
        <div className="flex items-center space-x-2">
          {documents.length > 0 && hasDocsToOcr && (
              <button
                onClick={(e) => { e.stopPropagation(); docsNeedOcr.forEach(d => onProcessLiasse(d)); }}
                className="px-2 py-1 text-xs font-medium text-white rounded bg-blue-600 hover:bg-blue-700 transition-colors"
              >
                <FiCpu className="inline h-3 w-3 mr-1" /> Lancer OCR
              </button>
          )}
          {isOpen ? <FiChevronUp className="h-4 w-4 text-slate-500" /> : <FiChevronDown className="h-4 w-4 text-slate-500" />}
        </div>
      </div>
      {isOpen && (
        <div id={`category-content-${categoryKey}`} className="border-t border-slate-200">
          {children}
        </div>
      )}
    </section>
  );
};

const FileUploadZone = ({ onFileUpload }) => {
  const [isDragging, setIsDragging] = useState(false);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      onFileUpload(e.dataTransfer.files[0]);
      e.dataTransfer.clearData();
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      onFileUpload(e.target.files[0]);
    }
  };

  return (
    <div 
      className={`p-4 border-2 border-dashed rounded-md text-center cursor-pointer transition-colors ${
        isDragging ? 'border-orange-500 bg-orange-50' : 'border-gray-300 hover:border-orange-400'
      }`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onClick={() => document.getElementById('liasse-upload-input').click()}
    >
      <FiUpload className="mx-auto h-6 w-6 text-gray-400" />
      <p className="mt-2 text-sm text-gray-600">
        Déposez la liasse fiscale ici ou <span className="font-semibold text-orange-600">cliquez pour parcourir</span>
      </p>
      <p className="text-xs text-gray-500 mt-1">PDF uniquement</p>
      <input
        id="liasse-upload-input"
        type="file"
        className="hidden"
        accept="application/pdf"
        onChange={handleFileChange}
      />
    </div>
  );
};

// ===== COMPOSANT SECTION COMPACT =====
const WorkflowStepSection = ({
  stepIndex,
  title,
  subtitle,
  icon: Icon,
  isActive,
  isOpen,
  onToggle,
  children
}) => {
  return (
    <div
      className={`bg-white rounded-md shadow-sm border transition-all duration-200 ${
        isActive ? 'border-orange-500 border-2' : 'border-gray-200'
      }`}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-2 text-left hover:bg-gray-50 rounded-t-md"
        aria-expanded={isOpen}
      >
        <div className="flex items-center space-x-2 min-w-0">
          <div
            className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
              isActive ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-700'
            }`}
          >
            {stepIndex + 1}
          </div>

          <div className="min-w-0">
            <h2 className="text-xs font-semibold text-gray-900 truncate">{title}</h2>
            {subtitle && (
              <p className="text-[10px] text-gray-600 mt-0.5 line-clamp-1">
                {subtitle}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {Icon && <Icon className={`h-3.5 w-3.5 ${isActive ? 'text-orange-600' : 'text-gray-500'}`} />}
          {isOpen ? (
            <FiChevronUp className="h-4 w-4 text-gray-500" />
          ) : (
            <FiChevronDown className="h-4 w-4 text-gray-500" />
          )}
        </div>
      </button>

      {isOpen && (
        <div className="border-t p-2 bg-gray-50/50">
          {children}
        </div>
      )}
    </div>
  );
};

// ===== INDICATEUR D'ÉTAPES COMPACT =====
const SectionTitle = ({ title, subtitle, icon: Icon, badge, children }) => {
  return (
    <div className="bg-white rounded-md shadow-sm border border-gray-200 mb-2">
      <div className="p-2 border-b border-gray-200">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center space-x-2 min-w-0">
            {Icon && <Icon className="h-3.5 w-3.5 text-orange-600" />}
            <div className="min-w-0">
              <h2 className="text-xs font-semibold text-gray-900 truncate">{title}</h2>
              {subtitle && <p className="text-[10px] text-gray-600 mt-0.5 truncate">{subtitle}</p>}
            </div>
          </div>

          {badge !== undefined && badge !== null && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-orange-100 text-orange-800 shrink-0">
              {badge}
            </span>
          )}
        </div>
      </div>

      <div className="p-2">
        {children}
      </div>
    </div>
  );
};

// ===== COMPOSANT PRINCIPAL =====
const AnalyseCspe = () => {
  const { session } = useOutletContext();
  const { accountId } = useParams(); // ✅ CORRIGÉ : Récupération depuis l'URL
  const location = useLocation();
  const navigate = useNavigate();
  
  // États pour la vue globale
  const [clientsWithPdls, setClientsWithPdls] = useState([]); 
  const [allAnalyses, setAllAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientSearch, setClientSearch] = useState('');
  
  const now = new Date();
  // ✅ Récupérer l'année transmise par TaxesDetail (ex: 2025)
  const passedYear = location.state?.year ? Number(location.state.year) : null;

  const [selectedYear, setSelectedYear] = useState(() =>
    accountId ? (passedYear ?? 2023) : now.getFullYear()
  );

  const [selectedMonth, setSelectedMonth] = useState(
    typeof location.state?.month === 'number' ? location.state.month : 0
  );

  // Contrats taxe signés (source = /api/documents)
  const [signedTaxContracts, setSignedTaxContracts] = useState([]);

  // UI table (comme SignedContractsTable)
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    status: '', // optionnel si tu veux plus tard (ex: signed)
  });
  const [sortConfig, setSortConfig] = useState({ key: 'signed_at', direction: 'desc' });

  // États pour la vue détaillée
  const [client, setClient] = useState(null);
  const [pdlList, setPdlList] = useState([]);
  const [allUserDocuments, setAllUserDocuments] = useState([]);
  const [fiscalData, setFiscalData] = useState(null);
  const [summaryERA, setSummaryERA] = useState(null);
  const [generatedCerfas, setGeneratedCerfas] = useState([]);
  
  const [isGeneratingERA, setIsGeneratingERA] = useState(false);
  const [isGeneratingTICVA, setIsGeneratingTICVA] = useState(false);
  const [electroIntensite, setElectroIntensite] = useState(null);
  const [isGenerating3310TIC, setIsGenerating3310TIC] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isProcessingLiasse, setIsProcessingLiasse] = useState(false);
  const [isMarkingVerificationComplete, setIsMarkingVerificationComplete] = useState(false);
  const [isConfirmingCerfaGen, setIsConfirmingCerfaGen] = useState(false);
  const [isCombiningCerfas, setIsCombiningCerfas] = useState(false);
  const [isNotifying, setIsNotifying] = useState(false);

  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [signedCerfaDocuments, setSignedCerfaDocuments] = useState([]);
  const [success, setSuccess] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isEraSectionOpen, setIsEraSectionOpen] = useState(true);
  const [notificationHistory, setNotificationHistory] = useState([]);
  const [historyVisible, setHistoryVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('analyse');
  const [generatingPdlId, setGeneratingPdlId] = useState(null);
  const [openStepIndex, setOpenStepIndex] = useState(0);
  const prevWorkflowStepRef = useRef(null);
  const [dossierYear, setDossierYear] = useState(null);
  const [isTicfeModalOpen, setIsTicfeModalOpen] = useState(false);
  const [ticfeModalPdl, setTicfeModalPdl] = useState(null);
  const [isSiretModalOpen, setIsSiretModalOpen] = useState(false);
  const [siretModalPdl, setSiretModalPdl] = useState(null);
  const [isExoModalOpen, setIsExoModalOpen] = useState(false);
  // Régénération d'un ERA refusée car l'exercice est verrouillé (409 DOSSIER_VERROUILLE) : { pdl, message }
  const [verrouEra, setVerrouEra] = useState(null);
  const [exoModalPdl, setExoModalPdl] = useState(null);
  const [isRaisonSocialeModalOpen, setIsRaisonSocialeModalOpen] = useState(false);
  const [premierBilanConfig, setPremierBilanConfig] = useState({});
  const [manualLinesCountByPdl, setManualLinesCountByPdl] = useState({}); // clé: pdlId → count
  const [manualModeFlags, setManualModeFlags] = useState({}); // clé: pdlId → boolean

  const MOIS = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];

  useEffect(() => {
  const y = new Date().getFullYear();
  if (accountId) {
    setSelectedYear(passedYear ?? 2023);  // ✅ utilise l'année transmise
  } else {
    setSelectedYear(y);
  }
}, [accountId]); 

  function periodLabel(year, month) {
    if (!year) return '';
    if (!month) return `Année ${year}`;
    return `${MOIS[month - 1]} ${year}`;
  }

  const openTicfeEditor = (pdl) => {
    setTicfeModalPdl(pdl);
    setIsTicfeModalOpen(true);
  };

  const openSiretEditor = (pdl) => {
    setSiretModalPdl(pdl);
    setIsSiretModalOpen(true);
  };

  const openExoEditor = (pdl) => {
    setExoModalPdl(pdl);
    setIsExoModalOpen(true);
  };

  // Met à jour pdlList en mémoire après save (évite un refetch complet)
  const handlePdlSaved = (updatedPdl) => {
    setPdlList((prev) => prev.map((p) => (p.id === updatedPdl.id ? { ...p, ...updatedPdl } : p)));
  };
  // ===== TOUTES VOS FONCTIONS EXISTANTES - AVEC CORRECTIONS =====
  
  const fetchGeneratedCerfas = useCallback(async () => {
  if (!accountId) {
    console.warn("fetchGeneratedCerfas: accountId manquant.");
    setGeneratedCerfas([]);
    return;
  }

  try {
    const { data, error } = await supabase
      .from('generated_cerfa_documents')
      .select('*')
      .eq('account_id', accountId)
      .eq('fiscal_year', selectedYear) // ✅ AJOUT ICI
      .order('generated_at', { ascending: false });

    if (error) throw error;
    setGeneratedCerfas(data || []);
  } catch (error) {
    console.error("[fetchGeneratedCerfas] Erreur chargement CERFAs:", error.message);
  }
}, [accountId, selectedYear]);

  // Charger l'intensité depuis valeur_ajoutee_calculee
  const fetchElectroIntensite = useCallback(async () => {
    if (!accountId) return;
    try {
      const { data } = await supabase
        .from('valeur_ajoutee_calculee')
        .select('intensite_calculee')
        .eq('account_id', accountId)
        .eq('annee_fiscale', selectedYear)
        .eq('source_cerfa_type', 'CERFA_TIC_VA')
        .maybeSingle();
      if (data) {
        setElectroIntensite(data.intensite_calculee); // null = VA négative → OK
      }
    } catch (e) {
      // silencieux
    }
  }, [accountId, selectedYear]);

  const fetchClientData = useCallback(async () => {
    setLoading(true);
  
    try {
      // 1. On récupère les infos de base (Vue)
      const { data: clientRow, error: clientViewError } = await supabase
        .from("clients_view")
        .select("*")
        .eq("account_id", accountId) 
        .single();
  
      if (clientViewError) throw clientViewError;
      if (!clientRow) {
        setClient(null);
        setLoading(false);
        return;
      }
  
      // 2. Récupération des données Pappers
      let pappersRow = null;
      if (clientRow.siren) {
        const { data: pappersOne, error: pappersErr } = await supabase
          .from("pappers_data")
          .select("*")
          .eq("siren", clientRow.siren)
          .order("updated_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        if (pappersErr) console.warn("[fetchClientData] ⚠️ pappers_data error:", pappersErr.message);
        else pappersRow = pappersOne || null;
      }
  
      // 3. 🟣 Récupération des métadonnées métier dans ACCOUNTS (Source de vérité)
      const { data: accountSpecifics, error: accountSpecificsError } = await supabase
        .from("accounts") // 🟣 Pivot sur accounts
        .select(`
          meter_reference, 
          dossier_stage, 
          dossier_progress_percentage, 
          onboarding_completed,
          contract_signed_at, 
          collecte_docs_completed_at, 
          admin_docs_verified_at,
          admin_cerfa_generation_completed_at, 
          cerfa_docusign_envelope_id, 
          cerfa_signed_at,
          admin_dossier_rejected_at, 
          admin_dossier_completed_at, 
          cerfa_bundle_generated_at,
          cerfa_bundle_storage_path
        `)
        .eq("id", accountId) // 🟣 Clé primaire de accounts
        .maybeSingle();
  
      if (accountSpecificsError) console.warn("[fetchClientData] ⚠️ accounts error:", accountSpecificsError.message);
  
      // 4. 🟣 Fusion des données (Correction de la variable profileSpecifics -> accountSpecifics)
      let enrichedClientData = { 
        ...clientRow, 
        pappers_data: pappersRow, 
        ...(accountSpecifics || {})
      };

      // 🟣 Synchronisation de la référence compteur pour l'affichage
      if (enrichedClientData.meter_reference && enrichedClientData.pappers_data) {
        enrichedClientData.pappers_data.meter_reference = enrichedClientData.meter_reference;
      }
      setClient(enrichedClientData);

      // 4b. Récupération des données annuelles (cspe_dossier_years)
      const dossierYearRes = await supabase
        .from('cspe_dossier_years')
        .select('*')
        .eq('account_id', accountId)
        .eq('fiscal_year', selectedYear)
        .maybeSingle();
      setDossierYear(dossierYearRes.data || null);

      // 5. Chargement des données liées au compte
      const [
        docsRes,
        fiscalResRaw,
        eraSummaryResRaw,
        signedCerfasRes,
        notificationsRes,
        pdlsRes, 
      ] = await Promise.all([
        supabase.from("user_uploaded_documents").select("*").eq("account_id", accountId), 
        // Convention article 1586 quinquies CGI : annee_fiscale = année calendaire de clôture.
        supabase.from("fiscal_declarations_data").select("*").eq("account_id", accountId).gte("exercice_end_date", `${selectedYear}-01-01`).lte("exercice_end_date", `${selectedYear}-12-31`).order("exercice_end_date", { ascending: false }).limit(1),
        supabase.from("cerfa_era_summary_data").select("*").eq("account_id", accountId).eq("annee_fiscale", selectedYear).order("exercice_end_date", { ascending: false }).limit(1),
        supabase.from("signed_cerfas").select("*")
        .eq("account_id", accountId)
        .eq("fiscal_year", selectedYear)
        .order("signed_at", { ascending: false }),
        supabase.from("cerfa_notifications").select("*").eq("account_id", accountId).order('notified_at', { ascending: false }), 
        // select('*') et non une liste de colonnes : les colonnes exoneration_* (migration 029)
        // n'existent pas tant que la migration n'est pas appliquée. Les nommer ferait échouer
        // toute la requête (42703) et vider l'écran ; avec '*', un PDL sans ces colonnes est
        // simplement lu comme non exonéré (cspeGating.estPdlExonere).
        supabase.from("pdl").select("*").eq("account_id", accountId),
      ]);
  
      if (docsRes.error) throw docsRes.error;
      if (signedCerfasRes.error) throw signedCerfasRes.error;
      if (pdlsRes.error) throw pdlsRes.error;

      if (pdlsRes.data) {
        setPdlList(pdlsRes.data);
      }

      const fiscalRow = (fiscalResRaw.data && fiscalResRaw.data[0]) || null;
      const eraSummaryRow = (eraSummaryResRaw.data && eraSummaryResRaw.data[0]) || null;
  
      const normalizedDocs = (docsRes.data || []).map(d => ({
        ...d,
        ocr_done: !!d.ocr_done,
        ocrDone: !!d.ocr_done,
        year: d.year != null ? Number(d.year) : null,    // ✅ toujours number
      }));
      setAllUserDocuments(normalizedDocs);
      setSignedCerfaDocuments(signedCerfasRes.data || []);
      
      setNotificationHistory(notificationsRes.data || []);
  
      const fallbackFiscal = {
        exercice_end_date: null,
      };
      setFiscalData(fiscalRow || fallbackFiscal);
      setSummaryERA(eraSummaryRow || null);
  
    } catch (error) {
      console.error("[fetchClientData] 🔴 Erreur globale:", error.message);
      setClient(null);
      setAllUserDocuments([]);
      setFiscalData(null);
      setSummaryERA(null);
      setSignedCerfaDocuments([]);
      setNotificationHistory([]);
    } finally {
      setLoading(false);
    }
}, [accountId, selectedYear]);

  // Fonction pour charger la liste des clients (vue globale)
  const loadGlobalData = useCallback(async () => {
  if (!session?.access_token) return;
  setLoading(true);

  try {
    // ✅ FIX : Requête Supabase directe sur signed_at
    // L'API /api/documents filtre sur document.year quand month est absent → bugué
    let contractQuery = supabase
      .from('documents')
      .select(`
        id,
        account_id,
        type,
        status,
        signed_at,
        signed_document_url,
        metadata,
        account:accounts(id, company_name, status, siret)
      `)
      .eq('type', 'tax_contract')
      .eq('status', 'signed')
      .gte('signed_at', `${selectedYear}-01-01`)
      .lt('signed_at',  `${selectedYear + 1}-01-01`);

    // Filtre optionnel sur le mois
    if (selectedMonth && selectedMonth >= 1 && selectedMonth <= 12) {
      const m  = String(selectedMonth).padStart(2, '0');
      const nm = selectedMonth < 12
        ? `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-01`
        : `${selectedYear + 1}-01-01`;
      contractQuery = contractQuery
        .gte('signed_at', `${selectedYear}-${m}-01`)
        .lt('signed_at', nm);
    }

    const { data: contractsData, error: contractsError } = await contractQuery;
    if (contractsError) throw contractsError;

    const contracts = contractsData || [];
    setSignedTaxContracts(contracts);

    const signedAccountIds = new Set(
      contracts.map(d => String(d.account_id)).filter(Boolean)
    );
    // Analyses ERA (inchangé)
    const analysesResponse = await supabase
      .from('cerfa_era_summary_data')
      .select('*')
      .eq('annee_fiscale', selectedYear);

    if (analysesResponse?.data) setAllAnalyses(analysesResponse.data || []);

    // Clients avec PDL
    const clientsResp = await fetch(`/api/admin/clients-with-pdls`, {
      headers: { Authorization: `Bearer ${session.access_token}` },
    });
    const clientsJson = await clientsResp.json();

    if (!clientsJson?.success) {
      setClientsWithPdls([]);
      return;
    }

    const allClients  = clientsJson.data || [];
    const filteredClients = allClients.filter(c =>
      signedAccountIds.has(String(c.account_id))
    );

    setClientsWithPdls(filteredClients);

  } catch (e) {
    console.error('[AnalyseCSPE] loadGlobalData error', e);
    setSignedTaxContracts([]);
    setClientsWithPdls([]);
  } finally {
    setLoading(false);
  }
}, [session?.access_token, selectedYear, selectedMonth]);

  // Toutes vos fonctions métier sont conservées...
  const handleValidateDocument = async (docId) => {
    // 🟣 On cherche le document dans l'état local
    const doc = allUserDocuments.find(d => d.id === docId);

    // 🟣 SÉCURITÉ : Empêcher la validation d'une liasse non analysée
    if (doc?.document_type_id === 'documents_financiers' && !doc.ocr_done) {
        toast.error("Action impossible : Vous devez d'abord lancer l'analyse OCR du document.");
        return;
    }

    const toastId = toast.loading("Validation du document..."); 
    try {
      const { error } = await supabase
        .from('user_uploaded_documents')
        .update({ status: 'Validé', rejection_reason: null })
        .eq('id', docId);
      
      if (error) throw error;
      
      toast.success("Document validé avec succès !", { id: toastId }); 
      fetchClientData();
    } catch (error) {
      toast.error(`Erreur de validation : ${error.message}`, { id: toastId }); 
    }
  };

  const handleRejectDocument = async (docId, reason) => {
    if (!reason || reason.trim() === "") {
        // 🟣 Toast d'avertissement simple
        toast.error("Un motif de rejet est requis."); 
        return;
    }

    // 🟣 Création d'un toast de chargement
    const toastId = toast.loading("Enregistrement du rejet..."); 
    try {
      const { data, error } = await supabase
        .from('user_uploaded_documents')
        .update({ status: 'Rejeté', rejection_reason: reason })
        .eq('id', docId)
        .select().single();
      
      if (error) throw error;
      
      if (data) {
        setAllUserDocuments(prevDocs =>
          prevDocs.map(doc => (doc.id === docId ? { ...doc, status: 'Rejeté', rejection_reason: reason } : doc))
        );
      }
      
      // 🟣 Transformation du toast en succès
      toast.success("Document rejeté.", { id: toastId }); 
    } catch (err) {
      console.error('Erreur inattendue rejet:', err);
      // 🟣 Transformation du toast en erreur
      toast.error('Une erreur est survenue lors du rejet.', { id: toastId }); 
    }
  };

  const handleLiasseUpload = async (file) => {
    if (!file) return;

    const toastId = toast.loading('Préparation de l\'envoi de la liasse...');

    try {
        // Logique S3 en 3 étapes
        const { data: presignData } = await axios.post('/api/cspe/documents/request-upload-url', {
            fileName: file.name,
            accountId: accountId // ✅ CORRIGÉ
        }, {
            headers: { 'Authorization': `Bearer ${session.access_token}` }
        });

        if (!presignData.success) throw new Error("Impossible d'obtenir une URL d'upload.");

        toast.loading(`Envoi du fichier en cours... (${formatFileSize(file.size)})`, { id: toastId });
        await axios.put(presignData.data.uploadUrl, file, {
            headers: { 'Content-Type': file.type },
            // Borne un envoi qui cale (gros fichier / connexion lente) au lieu d'un hang infini
            // qui laissait l'UI figée sur « envoi en cours ». 10 min > temps d'upload d'un PDF de ~20 Mo.
            timeout: 10 * 60 * 1000,
            onUploadProgress: (event) => {
                const pct = computeUploadPercent(event);
                toast.loading(
                    pct != null
                        ? `Envoi du fichier en cours... ${pct}%`
                        : `Envoi du fichier en cours... (${formatFileSize(file.size)})`,
                    { id: toastId }
                );
            },
        });

        toast.loading('Finalisation...', { id: toastId });
        const payload = {
            s3Key: presignData.data.s3Key,
            originalFileName: file.name,
            fileType: file.type,
            fileSize: file.size,
            type: 'documents_financiers',
            year: selectedYear,
            accountId: accountId // ✅ CORRIGÉ
        };

        await axios.post('/api/cspe/documents/confirm-upload', payload, {
            headers: { 'Authorization': `Bearer ${session.access_token}` }
        });

        toast.success('Liasse fiscale envoyée avec succès !', { id: toastId });
        fetchClientData();
    } catch (error) {
        toast.error(uploadErrorMessage(error), { id: toastId });
    }
  };

  const handleDeleteLiasse = async (doc) => {
    const confirmed = await confirmToast({
      title: 'Supprimer cette liasse fiscale ?',
      subtitle: `"${doc.original_file_name}" sera définitivement supprimée.`,
      confirmLabel: 'Supprimer',
      variant: 'danger',
    });
    if (!confirmed) return;

    const toastId = toast.loading('Suppression en cours...');
    try {
      // 1. Suppression du fichier S3
      const { error: storageError } = await supabase.storage
        .from(doc.s3_bucket)
        .remove([doc.s3_key]);

      if (storageError) throw new Error(storageError.message);

      // 2. Suppression de l'entrée en base
      const { error: dbError } = await supabase
        .from('user_uploaded_documents')
        .delete()
        .eq('id', doc.id)
        .eq('account_id', accountId);

      if (dbError) throw new Error(dbError.message);

      toast.success('Liasse fiscale supprimée.', { id: toastId });
      fetchClientData(); // ✅ rafraîchit → zone d'upload réapparaît automatiquement
    } catch (error) {
      console.error('❌ Erreur suppression liasse:', error);
      toast.error(`Suppression échouée : ${error.message}`, { id: toastId });
    }
  };

  const handleProcessLiasse = useCallback(async (doc) => {
    if (!doc || !client || !client.account_id) {
      toast.error("Infos client ou document manquantes.");
      return;
    }
    if (isProcessingLiasse) return;

    setIsProcessingLiasse(true);
    const toastId = toast.loading("Analyse OCR de la liasse fiscale en cours...");

    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) throw new Error("Session invalide ou expirée.");

      const token = session.access_token;
      const storagePath = buildStoragePath(doc);

      const response = await fetch(`/api/cspe/ocr/process-liasse-fiscale`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          storagePath,
          documentId: doc.id,
          accountId: client.account_id
        })
      });

      let result;
      try { result = await response.json(); }
      catch { throw new Error(`Erreur backend (${response.status})`); }

      if (!response.ok || !result?.success) {
        throw new Error(result?.message || "Échec traitement liasse.");
      }

      // ✅ Le backend (liasseDataService) sauvegarde déjà fiscal_declarations_data
      // ✅ Pas de doublon : on se contente de marquer le document comme analysé

      if (result.data) {
        // ✅ 1. Marquer le document : traité + statut visible dans l'UI
        const { error: updError } = await supabase
          .from('user_uploaded_documents')
          .update({
            ocr_done: true,
            status: 'En attente de validation',   // ← AJOUT CRITIQUE
          })
          .eq('id', doc.id);

        if (updError) console.error("Erreur update document post-OCR :", updError);
      }

      await fetchClientData(); // ← déclenche le re-render avec le nouveau statut
      toast.success("Liasse fiscale traitée — en attente de validation.", { id: toastId });

    } catch (error) {
      console.error("Erreur liasse fiscale:", error);
      toast.error(`Erreur: ${error.message}`, { id: toastId });
    } finally {
      setIsProcessingLiasse(false);
    }
  }, [client, isProcessingLiasse, fetchClientData]);

  const handleValidateAllInCategory = async (documentsToValidate) => {
    // 1. Vérification si la liste est vide
    if (documentsToValidate.length === 0) {
      toast.error("Aucun document 'En attente' à valider.");
      return;
    }

    // 🟣 2. SÉCURITÉ : Bloquer si des liasses n'ont pas encore été analysées (OCR)
    const unparsedLiasses = documentsToValidate.filter(doc => 
      doc.document_type_id === 'documents_financiers' && !doc.ocr_done
    );

    if (unparsedLiasses.length > 0) {
      toast.error("Validation impossible : Une ou plusieurs liasses fiscales n'ont pas été analysées (OCR).");
      return;
    }

    // 3. Confirmation utilisateur
    const confirmed = await confirmToast({
      title: `Valider ${documentsToValidate.length} document${documentsToValidate.length > 1 ? 's' : ''} ?`,
      subtitle: 'Cette action marquera les documents sélectionnés comme "Validé".',
      confirmLabel: 'Valider',
      cancelLabel: 'Annuler',
      variant: 'warning',
    });
    if (!confirmed) return;

    const toastId = toast.loading(`Validation de ${documentsToValidate.length} document(s) en cours...`);

    try {
      // 🟣 5. Création de la liste des promesses de mise à jour
      const validationPromises = documentsToValidate.map(async (doc) => {
        const { error } = await supabase
          .from('user_uploaded_documents')
          .update({ status: 'Validé', rejection_reason: null })
          .eq('id', doc.id);
        
        if (error) throw new Error(`Échec pour "${doc.original_file_name}": ${error.message}`);
        return true;
      });

      // 🟣 6. Exécution en parallèle
      const results = await Promise.allSettled(validationPromises);
      const successCount = results.filter(r => r.status === 'fulfilled').length;
      const errorCount = results.filter(r => r.status === 'rejected').length;

      // 🟣 7. Résultat final via Toasts (on supprime l'alertMessage et l'alert)
      if (errorCount === 0) {
        toast.success(`${successCount} document(s) validé(s) avec succès !`, { id: toastId });
      } else {
        toast.error(`${successCount} validé(s), ${errorCount} échec(s). Détails en console.`, { id: toastId, duration: 5000 });
        // Log des erreurs pour le debug technique
        results.forEach(r => { if (r.status === 'rejected') console.error(r.reason); });
      }

      // 🟣 8. Rafraîchissement des données
      fetchClientData();

    } catch (globalError) {
      console.error("Erreur critique validation lot:", globalError);
      toast.error("Une erreur inattendue est survenue.", { id: toastId });
    }
  }; // 🟣 Fin propre de la fonction

  // options : { forcer, motif } — confirmation explicite d'une régénération sur exercice verrouillé.
  const handleGenerateCerfaERA = useCallback(async (pdl, options = {}) => {
    const year = selectedYear;
    const pdlId = pdl.id;

    if (!client || !year) {
      toast.error("Données client ou année d'analyse manquantes.");
      return;
    }

    // Prérequis : liasse fiscale et TIC VA doivent être traités — sauf PDL exonéré (E04),
    // dont l'ERA se génère sans liasse ni TIC-VA (le backend applique la même règle).
    if (!pdl.exoneration_motif && !fiscalData?.id) {
      toast.error("La liasse fiscale doit être traitée avant de générer l'ERA.");
      return;
    }

    // 1. Filtrer les factures pour ce PDL et cette année
    const docsToProcess = (allUserDocuments || []).filter(
      doc =>
        doc.pdl_id === pdlId &&
        Number(doc.year) === year &&
        (doc.document_type_id === 'factures_electricite' || doc.document_type_id === 'facture_electricite')
    );

    const hasManual = (manualLinesCountByPdl[pdlId] || 0) > 0;
    const isManualOnly = docsToProcess.length === 0 && hasManual;

    if (docsToProcess.length === 0 && !hasManual) {
      toast.error(`Aucune facture ni ligne manuelle pour ${pdl.site_name} en ${year}.`);
      return;
    }

    setGeneratingPdlId(pdlId);
    const toastId = isManualOnly
      ? toast.loading(`Génération CERFA (mode manuel) pour ${pdl.site_name}...`)
      : toast.loading(`Lancement du pipeline d'analyse pour ${pdl.site_name}...`);

    try {
      const token = session?.access_token;
      if (!token) throw new Error("Session expirée.");

      // En mode manuel pur, on saute les étapes OCR (validation + identification fournisseurs)
      if (!isManualOnly) {
        // =========================================================
        // ÉTAPE 1 : VALIDATION DES DOCUMENTS
        // =========================================================
        toast.loading(`Étape 1/3 : Validation des factures...`, { id: toastId });
        await Promise.all(docsToProcess.map(doc =>
          axios.post(`/api/cspe/documents/uploads-s3/${doc.id}/validate`, {}, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
        ));

        // =========================================================
        // ÉTAPE 2 : IDENTIFICATION DES FOURNISSEURS
        // =========================================================
        toast.loading(`Étape 2/3 : Identification des fournisseurs...`, { id: toastId });
        // L'ERA se calcule sur les lignes CSPE déjà enregistrées (OCR, corrections,
        // saisie manuelle) — cf. utils/identificationFournisseurs.js. Factures déjà
        // identifiées : pas de nouvel appel (téléchargement S3 + PDF.js + Gemini payant).
        const aIdentifier = docsToProcess.filter(doc => !doc.fournisseur_electricite);
        const nonIdentifiees = await identifierFournisseursSansBloquer(aIdentifier, doc =>
          axios.post('/api/cspe/ocr/identify-provider', {
            documentId: doc.id,
            storagePath: doc.s3_key
              ? `s3://${doc.s3_bucket}/${doc.s3_key}`
              : doc.old_supabase_storage_path,
          }, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
        );
        if (nonIdentifiees.length > 0 && !hasManual) {
          // Sans aucune ligne manuelle sur ce PDL, ces factures n'apporteraient rien à
          // l'ERA : le générer sous-estimerait la CSPE sans que personne ne le voie.
          throw new Error(`${nonIdentifiees.length} facture(s) au fournisseur non reconnu et aucune `
            + `ligne saisie à la main pour ce PDL : saisissez leurs lignes CSPE avant de générer l'ERA.`);
        }
        if (nonIdentifiees.length > 0) {
          nonIdentifiees.forEach(({ doc, message }) =>
            console.warn(`Identification fournisseur échouée (${doc.original_file_name}) : ${message}`));
          toast(avertissementNonIdentifiees(nonIdentifiees), { icon: '⚠️', duration: 9000 });
        }
      }

      // =========================================================
      // GÉNÉRATION DU CERFA ERA
      // (utilise fetchTicfeEffectiveLines côté backend = OCR + overrides + lignes manuelles)
      // =========================================================
      toast.loading(isManualOnly ? `Génération du CERFA ERA (lignes manuelles)...` : `Étape 3/3 : Génération du CERFA ERA...`, { id: toastId });

      const pbConfig = premierBilanConfig[`${pdlId}-${year}`];

      const response = await fetch('/api/cspe/documents/generate-era', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          account_id: client.account_id,
          pdl_id: pdlId,
          annee_fiscale: year,
          fiscal_year: year,
          ...(options.forcer ? { forcer_regeneration: true, motif_regeneration: options.motif } : {}),
          ...(pbConfig?.enabled && pbConfig?.startDate
            ? { exercice_start_date: pbConfig.startDate }
            : {}
          ),
        })
      });

      const result = await response.json();
      // Exercice verrouillé (prêt pour la signature, signé…) : confirmation motivée demandée.
      if (response.status === 409 && result.code === 'DOSSIER_VERROUILLE') {
        toast.dismiss(toastId);
        setVerrouEra({ pdl, message: result.message });
        return;
      }
      if (!response.ok) throw new Error(result.message || "Erreur lors de la génération PDF.");

      // FIN DU PIPELINE — débloquer l'auto-advance pour la suite du workflow
      manualModeLockRef.current = false;
      toast.success(`CERFA généré pour ${pdl.site_name} !`, { id: toastId });
      // Majoration ZNI non séparée sur certaines lignes (prix facturé non reconnu) : à vérifier.
      if (result.avertissements?.length) {
        toast(`À vérifier — ${result.avertissements.join('\n')}`, { icon: '⚠️', duration: 15000 });
      }

      // Mise à jour de la vue
      await Promise.all([
        fetchGeneratedCerfas(),
        fetchClientData()
      ]);

    } catch (error) {
      console.error("Erreur Pipeline Analyse:", error);
      // Message du backend s'il y en a un (axios ne donne sinon que « Request failed with status code … »).
      toast.error(`Erreur : ${error.response?.data?.message || error.message}`, { id: toastId, duration: 6000 });
    } finally {
      setGeneratingPdlId(null);
    }
  }, [client, selectedYear, allUserDocuments, session, fetchGeneratedCerfas, fetchClientData, manualLinesCountByPdl, premierBilanConfig, fiscalData]);


  const handleGenerateCerfaTICVA = async () => {
    // Vérification initiale
    if (!client || !client.account_id || !fiscalData || !fiscalData.id) {
      toast.error("Les données du client ou l'ID des données fiscales sont manquantes.");
      return;
    }

    // Vérification de la présence de la liasse fiscale validée
    if (!isLiasseValidated || !fiscalData?.id) {
      toast.error("La liasse fiscale doit être importée et validée avant de générer le CERFA TIC-VA.");
      return;
    }

    setIsGeneratingTICVA(true);
    const toastId = toast.loading("Génération du CERFA TIC-VA en cours..."); 

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Votre session est invalide.");

      // Toggle « Premier bilan » : la config est par PDL, mais l'exercice
      // fiscal est commun à tout le compte — n'importe quelle entrée activée
      // pour l'année sélectionnée porte donc la même date de début.
      // Sans ça, la date corrigée à la main n'atteignait que le CERFA ERA :
      // le TIC-VA repartait sur la BDD et imprimait 12 mois.
      const pbStartDate = Object.entries(premierBilanConfig)
        .find(([key, cfg]) => key.endsWith(`-${selectedYear}`) && cfg?.enabled && cfg?.startDate)
        ?.[1]?.startDate;

      // ✅ Payload corrigé pour correspondre à ce que le Backend attend (accountId)
      const payload = {
        fiscal_declaration_id: fiscalData.id,
        accountId: client.account_id, // <--- La correction est ici
        annee_fiscale: selectedYear,
        fiscal_year: selectedYear,
        ...(pbStartDate ? { exercice_start_date: pbStartDate } : {}),
      };

      const response = await fetch('/api/cspe/documents/generate-tic-va', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify(payload)
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'Erreur serveur.');
      
      toast.success('CERFA TIC-VA généré avec succès !', { id: toastId });
      if (result.intensiteCalculee !== undefined) {
        setElectroIntensite(result.intensiteCalculee);
      }
      manualModeLockRef.current = false;
      await Promise.all([fetchGeneratedCerfas(), fetchClientData()]);

    } catch (error) {
      toast.error(`Erreur : ${error.message}`, { id: toastId }); 
    } finally {
      setIsGeneratingTICVA(false);
    }
  };

  const handleGenerateCerfa3310TIC = async () => {
    // 1. SÉCURITÉ : la liasse est exigée, sauf si TOUS les PDL avec données de l'année sont
    // exonérés (E04) : le backend génère alors le 3310 sans liasse (fiscal_declaration_id facultatif).
    // pdlsAvecDataPourAnnee est déclaré plus bas dans le composant : lu ici au clic, après le rendu.
    const sansLiasse = tousPdlsExoneres(pdlsAvecDataPourAnnee);
    if (!client || !client.account_id) {
      toast.error("Les données du client sont manquantes.");
      return;
    }
    if (!sansLiasse && !fiscalData?.id) {
      toast.error("Les données du client ou fiscales sont manquantes.");
      return;
    }
    
    setIsGenerating3310TIC(true);
    const toastId = toast.loading("Génération du CERFA 3310-TIC..."); 

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Session invalide.");
      
      const payload = {
          // ✅ CORRECTION ICI : On utilise 'accountId' pour le Backend
          accountId: client.account_id,
          annee_fiscale: selectedYear,
          // Compte 100 % exonéré sans liasse : champ omis (le backend le traite comme facultatif).
          ...(fiscalData?.id ? { fiscal_declaration_id: fiscalData.id } : {}),
          fiscal_year: selectedYear,
      };
      
      const response = await fetch('/api/cspe/documents/generate-3310-tic', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}`},
        body: JSON.stringify(payload)
      });
      
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || "Erreur serveur.");
      
      toast.success('✅ CERFA 3310-TIC généré avec succès !', { id: toastId });
      manualModeLockRef.current = false;
      await Promise.all([fetchGeneratedCerfas(), fetchClientData()]);

    } catch (error) {
      toast.error(`Erreur : ${error.message}`, { id: toastId }); 
    } finally {
      setIsGenerating3310TIC(false);
    }
  };

  const handleDownloadGeneratedCerfa = async (storagePath, fileName, documentType) => {
  try {
    // 🔍 Déterminer le bucket selon le type de document
    const bucket = documentType === 'CERFA_BUNDLE' ? 'cerfa-bundles' : 'generated-cerfas';
    
    const { data, error } = await supabase.storage
      .from(bucket)
      .download(storagePath);
      
    if (error) throw error;
    downloadBlob(data, fileName);
  } catch (error) {
    console.error("Erreur téléchargement CERFA:", error);
    toast.error("Erreur lors du téléchargement.");
  }
};

  const handleDownloadCerfaBundle = async (storagePath, fileName) => {
    try {
      const { data, error } = await supabase.storage.from('cerfa-bundles').download(storagePath);
      if (error) throw error;
      downloadBlob(data, fileName);
    } catch (error) {
      console.error("Erreur téléchargement du bundle:", error);
      toast.error("Erreur de téléchargement du dossier combiné.");  // ✅
    }
  };

  const handleDownloadSignedCerfaBundle = async (storagePath, fileName) => {
      try {
        if (!storagePath || typeof storagePath !== 'string' || storagePath.trim() === '') {
          toast.error("Chemin du document manquant.");  // ✅
          return;
        }
        const cleanStoragePath = storagePath.startsWith('/') ? storagePath.substring(1) : storagePath;
        const { data, error } = await supabase.storage.from(SIGNED_CERFA_BUNDLES_BUCKET).download(cleanStoragePath);
        if (error) throw error;
        if (!data) throw new Error("Aucune donnée reçue.");
        downloadBlob(data, fileName || 'dossier_cerfa_signe.pdf');
      } catch (err) {
        console.error("Erreur téléchargement signé:", err);
        toast.error("Impossible de télécharger le dossier signé.");  // ✅
      }
    };

  const confirmDeleteCerfaToast = ({ title, description, confirmLabel = "Supprimer" }) =>
  new Promise((resolve) => {
    toast.custom(
      (t) => (
        <div
          style={{
            width: 320,
            background: "#fff",
            border: "1px solid rgba(0,0,0,.08)",
            borderRadius: 8,
            padding: "10px 12px",
            boxShadow: "0 4px 16px rgba(0,0,0,.10)",
            fontSize: 12,
          }}
        >
          {/* Titre XS */}
          <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 4 }}>
            {title}
          </div>

          {/* Description XS */}
          {description ? (
            <div
              style={{
                color: "rgba(0,0,0,.65)",
                fontSize: 11,
                marginBottom: 10,
                whiteSpace: "pre-line",
                lineHeight: 1.4,
              }}
            >
              {description}
            </div>
          ) : null}

          {/* Boutons XS */}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 6 }}>
            <button
              onClick={() => { toast.dismiss(t.id); resolve(false); }}
              style={{
                padding: "4px 10px",
                borderRadius: 6,
                border: "1px solid rgba(0,0,0,.12)",
                background: "#fff",
                fontSize: 11,
                cursor: "pointer",
              }}
            >
              Annuler
            </button>

            <button
              onClick={() => { toast.dismiss(t.id); resolve(true); }}
              style={{
                padding: "4px 10px",
                borderRadius: 6,
                border: "none",
                background: "#b45309",
                color: "#fff",
                fontWeight: 700,
                fontSize: 11,
                cursor: "pointer",
              }}
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      ),
      { duration: Infinity, position: "top-center" }
    );
  });

  const handleDeleteGeneratedCerfa = async (cerfaDocId, storagePath, documentType) => {
  if (!cerfaDocId || !storagePath) {
    toast.error("Suppression annulée : informations manquantes (id/chemin).");
    return;
  }

  const cerfaToDelete = generatedCerfas.find((c) => c.id === cerfaDocId);
  const fileLabel = cerfaToDelete?.file_name ? `« ${cerfaToDelete.file_name} »` : "ce CERFA";

  // ✅ CONFIRMATION VIA TOAST (remplace window.confirm)
  const confirmed = await confirmDeleteCerfaToast({
    title: "Confirmer la suppression",
    description: `Vous êtes sur le point de supprimer :\n${fileLabel}\n\nCette action est définitive.`,
    confirmLabel: "Supprimer",
  });

  if (!confirmed) {
    toast("Suppression annulée.", { duration: 2000 });
    return;
  }

  const toastId = toast.loading(`Suppression en cours…\n${fileLabel}`, {
    id: `del-${cerfaDocId}`,
    style: { whiteSpace: "pre-line", maxWidth: 520 },
  });

  try {
    const bucket = documentType === "CERFA_BUNDLE" ? "cerfa-bundles" : "generated-cerfas";

    const { error: storageError } = await supabase.storage.from(bucket).remove([storagePath]);
    if (storageError) throw new Error(storageError.message);

    const { data: deletedRows, error: dbError } = await supabase
      .from("generated_cerfa_documents")
      .delete()
      .match({ id: cerfaDocId, account_id: client.account_id })
      .select("id");

    if (dbError) throw new Error(dbError.message);

    if (!deletedRows || deletedRows.length !== 1) {
      throw new Error(`Suppression incohérente (lignes supprimées: ${deletedRows?.length ?? 0}).`);
    }

    // ✅ nettoyage ERA summary : uniquement la ligne du PDL concerné
    if (cerfaToDelete && cerfaToDelete.document_type === "CERFA_ERA") {
      if (!cerfaToDelete.pdl_id) {
        console.warn("Suppression CERFA_ERA: pdl_id manquant, skip cerfa_era_summary_data delete");
      } else {
        const { error: eraError } = await supabase
          .from("cerfa_era_summary_data")
          .delete()
          .eq("account_id", client.account_id)
          .eq("pdl_id", cerfaToDelete.pdl_id)
          .eq("exercice_end_date", cerfaToDelete.exercice_end_date)
          .eq("annee_fiscale", selectedYear);

        if (eraError) throw new Error(eraError.message);
      }
    }

    toast.success(`CERFA supprimé.\n${fileLabel}`, {
      id: toastId,
      style: { whiteSpace: "pre-line", maxWidth: 520 },
    });

    fetchGeneratedCerfas();
  } catch (error) {
    toast.error(`Suppression impossible : ${error.message}`, { id: toastId });
  }
};

  const handlePreviewGeneratedCerfa = async (storagePath, documentType) => {
  try {
    const bucket = documentType === 'CERFA_BUNDLE' ? 'cerfa-bundles' : 'generated-cerfas';
    
    const { data, error } = await supabase.storage
      .from(bucket)
      .createSignedUrl(storagePath, 60);
      
    if (error || !data?.signedUrl) throw error || new Error("URL signée introuvable.");
    setPreviewUrl(data.signedUrl);
    setIsPreviewOpen(true);
  } catch (err) {
    toast.error("Impossible d'ouvrir l'aperçu.");
  }
};

  const handlePreviewCerfaBundle = async (storagePath) => {
      try {
        const { data, error } = await supabase.storage.from('cerfa-bundles').createSignedUrl(storagePath, 60);
        if (error || !data?.signedUrl) throw error || new Error("URL signée introuvable.");
        setPreviewUrl(data.signedUrl);
        setIsPreviewOpen(true);
      } catch (err) {
        toast.error("Impossible d'ouvrir l'aperçu du dossier combiné.");  // ✅
      }
    };

    const handlePreviewSignedCerfaBundle = async (storagePathOfSignedBundle) => {
      try {
        const cleanStoragePath = storagePathOfSignedBundle.startsWith('/') 
          ? storagePathOfSignedBundle.substring(1) 
          : storagePathOfSignedBundle;
        const { data, error } = await supabase.storage.from(SIGNED_CERFA_BUNDLES_BUCKET).createSignedUrl(cleanStoragePath, 300);
        if (error || !data?.signedUrl) throw new Error("Impossible de créer l'URL d'aperçu.");
        setPreviewUrl(data.signedUrl);
        setIsPreviewOpen(true);
      } catch (err) {
        toast.error("Impossible d'ouvrir l'aperçu du dossier signé.");  // ✅
      }
    };

  const handleMarkDocsVerified = () => {
    if (!client || !accountId) return;

    // 1. Définir la logique qui s'exécutera SI l'utilisateur confirme
    const executeVerification = async () => {
      setIsMarkingVerificationComplete(true);
      const loadingToastId = toast.loading("Validation en cours...");

      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) throw new Error("Session admin invalide.");

        const response = await fetch(`/api/cspe/admin/accounts/${accountId}/mark-docs-verified`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        });

        const result = await response.json();
        if (!response.ok) throw new Error(result.message || `Erreur serveur ${response.status}.`);

        toast.success("Vérification terminée avec succès !", { id: loadingToastId });
        fetchClientData(); // Mise à jour de l'UI

      } catch (error) {
        console.error("Erreur markDocsVerified:", error);
        toast.error(`Erreur : ${error.message}`, { id: loadingToastId });
      } finally {
        setIsMarkingVerificationComplete(false);
      }
    };

    // 2. Afficher le Toast de Confirmation (Custom UI)
    toast.custom((t) => (
      <div
        className={`${
          t.visible ? 'animate-enter' : 'animate-leave'
        } max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`}
      >
        <div className="flex-1 w-0 p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0 pt-0.5">
              <FiAlertTriangle className="h-10 w-10 text-yellow-500 rounded-full bg-yellow-100 p-2" />
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-gray-900">
                Confirmer la vérification ?
              </p>
              <p className="mt-1 text-sm text-gray-500">
                Voulez-vous marquer les documents de <strong>{client.company_name}</strong> comme vérifiés ?
              </p>
            </div>
          </div>
        </div>
        <div className="flex border-l border-gray-200">
          <button
            onClick={() => {
              toast.dismiss(t.id); // Fermer la question
              executeVerification(); // Lancer l'action
            }}
            className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-indigo-600 hover:text-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            Confirmer
          </button>
          <button
            onClick={() => toast.dismiss(t.id)}
            className="w-full border-t border-gray-200 rounded-none rounded-br-lg p-4 flex items-center justify-center text-sm font-medium text-gray-600 hover:text-gray-500 focus:outline-none"
          >
            Annuler
          </button>
        </div>
      </div>
    ), {
      duration: Infinity, // Important : ne disparaît pas tout seul
      position: 'top-center'
    });
  };

  const handleConfirmCerfaGeneration = () => {
    // 1. Vérifications de sécurité (On garde votre logique existante)
    if (!client || !accountId || !fiscalData?.exercice_end_date) {
        toast.error("Données manquantes pour confirmer la génération.");
        return;
    }

    const exerciceEndDateStr = new Date(fiscalData.exercice_end_date).toISOString().split('T')[0];
    
    // Vérification de la présence des 3 documents
    const eraGenerated = generatedCerfas.some(cf => cf.document_type === 'CERFA_ERA' && cf.exercice_end_date === exerciceEndDateStr);
    const ticvaGenerated = generatedCerfas.some(cf => cf.document_type === 'CERFA_TIC_VA' && cf.exercice_end_date === exerciceEndDateStr);
    const tic3310Generated = generatedCerfas.some(cf => cf.document_type === 'CERFA_3310_TIC' && cf.exercice_end_date === exerciceEndDateStr);

    if (!eraGenerated || !ticvaGenerated || !tic3310Generated) {
        toast.error(
            <div>
                <p className="font-bold">Documents manquants !</p>
                <p className="text-sm">Veuillez générer ERA, TIC-VA et 3310-TIC avant de valider.</p>
            </div>
        );
        return;
    }

    // 2. La logique d'exécution (API)
    const executeConfirmation = async () => {
        setIsConfirmingCerfaGen(true); // Utilisez votre state existant
        const toastId = toast.loading("Confirmation en cours...");

        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session?.access_token) throw new Error("Session admin invalide.");

            const response = await fetch(`/api/cspe/admin/accounts/${accountId}/mark-cerfa-generation-complete`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
                // On garde votre body avec la date, c'est utile pour le backend
                body: JSON.stringify({ exercice_end_date: exerciceEndDateStr, fiscal_year: selectedYear })
            });

            const result = await response.json();
            if (!response.ok) throw new Error(result.message || `Erreur serveur ${response.status}`);

            toast.success("Génération des CERFA confirmée !", { id: toastId });

            // Optimistic update : avancer le workflow immédiatement côté UI
            const nowIso = new Date().toISOString();
            setDossierYear(prev => prev ? { ...prev, admin_cerfa_generation_completed_at: nowIso, updated_at: nowIso } : prev);
            prevWorkflowStepRef.current = 5; // empêche le reset lors du refetch
            setOpenStepIndex(5); // ouvrir Étape 6

            fetchClientData(); // Rafraîchissement (ne doit pas reset openStepIndex)

        } catch (error) {
            console.error("Erreur confirmCerfa:", error);
            toast.error(`Erreur : ${error.message}`, { id: toastId });
        } finally {
            setIsConfirmingCerfaGen(false);
        }
    };

    // 3. Le Toast de Confirmation (Design Pro) au lieu du window.confirm
    toast.custom((t) => (
      <div className={`${t.visible ? 'animate-enter' : 'animate-leave'} max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`}>
        <div className="flex-1 w-0 p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0 pt-0.5">
              <FiCheckCircle className="h-10 w-10 text-green-500 rounded-full bg-green-100 p-2" />
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-gray-900">
                Tout est correct ?
              </p>
              <p className="mt-1 text-sm text-gray-500">
                Vous confirmez que les 3 CERFA (ERA, TIC-VA, 3310) sont générés et prêts pour la fusion ?
              </p>
            </div>
          </div>
        </div>
        <div className="flex border-l border-gray-200">
          <button
            onClick={() => {
              toast.dismiss(t.id);
              executeConfirmation();
            }}
            className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-green-600 hover:text-green-500 focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            Confirmer
          </button>
          <button
            onClick={() => toast.dismiss(t.id)}
            className="w-full border-t border-gray-200 rounded-none rounded-br-lg p-4 flex items-center justify-center text-sm font-medium text-gray-600 hover:text-gray-500 focus:outline-none"
          >
            Annuler
          </button>
        </div>
      </div>
    ), { duration: Infinity, position: 'top-center' });
  };

  const handleCombineCerfas = () => {
    // 1. Vérifications de sécurité
    if (!client || !client.account_id || !generatedCerfas || generatedCerfas.length === 0) {
        toast.error("Données manquantes ou aucun CERFA généré.");
        return;
    }

    // On s'assure de ne prendre que les CERFA de l'exercice en cours
    const exerciceEndDateStr = generatedCerfas[0].exercice_end_date;
    
    // Filtrage des documents sur cette date précise ; TIC-VA non exigée si tous les PDL avec
    // données sont exonérés. pdlsAvecDataPourAnnee est déclaré plus bas : lu ici au clic.
    const { chemins: cerfaPaths, manquants } = documentsAFusionner({
        cerfas: generatedCerfas,
        exerciceEndDate: exerciceEndDateStr,
        pdlsAvecData: pdlsAvecDataPourAnnee,
    });

    if (manquants.length > 0) {
        toast.error(`Impossible de combiner : il manque ${manquants.join(', ')}.`);
        return;
    }

    // 2. Logique d'exécution (API)

const executeCombination = async () => {
    setIsCombiningCerfas(true);
    const toastId = toast.loading("Fusion en cours...");

    try {
        const { data: { session } } = await supabase.auth.getSession();
        
        const payload = {
            accountId: client.account_id,
            account_id: client.account_id, // Doublon pour la sécurité
            exercice_end_date: exerciceEndDateStr,
            cerfa_paths: cerfaPaths,
            fiscal_year: selectedYear,
        };

        const response = await fetch('/api/cspe/documents/combine-cerfas', {
            method: 'POST',
            headers: { 
                'Content-Type': 'application/json', 
                'Authorization': `Bearer ${session?.access_token}` 
            },
            body: JSON.stringify(payload)
        });

        const result = await response.json();

        if (!response.ok) throw new Error(result.error || result.message || "Erreur fusion");

        toast.success("Succès !", { id: toastId });
        fetchGeneratedCerfas(); 
        // ✅ Les deux sont nécessaires :
        // fetchGeneratedCerfas → rafraîchit la table "Documents CERFA Générés" (CERFA_BUNDLE visible)
        // fetchClientData      → met à jour client.cerfa_bundle_generated_at → currentWorkflowStep = 6 → step 7 s'ouvre
        await Promise.all([
            fetchGeneratedCerfas(),
            fetchClientData(),          // ← AJOUT
        ]);

    } catch (error) {
        console.error("❌ [FRONTEND] Erreur attrapée:", error);
        toast.error(`Erreur : ${error.message}`, { id: toastId });
    } finally {
        setIsCombiningCerfas(false);
    }
};

    // 3. Toast de Confirmation (UI Pro)
    toast.custom((t) => (
      <div className={`${t.visible ? 'animate-enter' : 'animate-leave'} max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`}>
        <div className="flex-1 w-0 p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0 pt-0.5">
              <FiLayers className="h-10 w-10 text-indigo-500 rounded-full bg-indigo-100 p-2" />
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-gray-900">
                Fusionner le dossier ?
              </p>
              <p className="mt-1 text-sm text-gray-500">
                Cela va créer un fichier PDF unique contenant tous les CERFA, prêt pour la signature.
              </p>
            </div>
          </div>
        </div>
        <div className="flex border-l border-gray-200">
          <button
            onClick={() => {
              toast.dismiss(t.id);
              executeCombination();
            }}
            className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-indigo-600 hover:text-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            Fusionner
          </button>
          <button
            onClick={() => toast.dismiss(t.id)}
            className="w-full border-t border-gray-200 rounded-none rounded-br-lg p-4 flex items-center justify-center text-sm font-medium text-gray-600 hover:text-gray-500 focus:outline-none"
          >
            Annuler
          </button>
        </div>
      </div>
    ), { duration: Infinity, position: 'top-center' });
  };

  const handleDeleteCerfaBundle = () => {
    // 1. Vérification (utilise dossierYear pour le chemin annuel)
    if (!client || !dossierYear?.cerfa_bundle_storage_path) return;

    // 2. Logique de suppression
    const executeDeletion = async () => {
        const toastId = toast.loading("Suppression du dossier en cours...");
        try {
            const token = (await supabase.auth.getSession()).data.session?.access_token;
            if (!token) throw new Error("Session invalide.");

            const response = await fetch(`/api/cspe/admin/accounts/${client.account_id}/delete-cerfa-bundle?fiscal_year=${selectedYear}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` },
            });

            const result = await response.json();
            if (!response.ok) throw new Error(result.message || 'Erreur suppression.');

            toast.success("Dossier combiné supprimé définitivement.", { id: toastId });
            await fetchClientData(); // Mise à jour UI

        } catch (error) {
            console.error("Erreur delete:", error);
            toast.error(`Erreur: ${error.message}`, { id: toastId });
        }
    };

    // 3. Toast de Confirmation (Style DANGER)
    toast.custom((t) => (
      <div className={`${t.visible ? 'animate-enter' : 'animate-leave'} max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`}>
        <div className="flex-1 w-0 p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0 pt-0.5">
              <FiTrash2 className="h-10 w-10 text-red-500 rounded-full bg-red-100 p-2" />
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-gray-900">
                Supprimer le dossier combiné ?
              </p>
              <p className="mt-1 text-sm text-gray-500">
                Attention, cela supprimera le fichier PDF unique contenant tous les CERFA. Cette action est irréversible.
              </p>
            </div>
          </div>
        </div>
        <div className="flex border-l border-gray-200">
          <button
            onClick={() => {
              toast.dismiss(t.id);
              executeDeletion();
            }}
            className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-red-600 hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            Supprimer
          </button>
          <button
            onClick={() => toast.dismiss(t.id)}
            className="w-full border-t border-gray-200 rounded-none rounded-br-lg p-4 flex items-center justify-center text-sm font-medium text-gray-600 hover:text-gray-500 focus:outline-none"
          >
            Annuler
          </button>
        </div>
      </div>
    ), { duration: Infinity, position: 'top-center' });
  };

  const handleSendYousignRequest = () => {
    // 1. Vérifications de base (utilise dossierYear pour le chemin annuel)
    if (!client || !dossierYear?.cerfa_bundle_storage_path) {
        toast.error("Le dossier PDF combiné doit être généré avant l'envoi.");
        return;
    }

    // 2. La logique d'envoi (encapsulée)
    const executeSending = async () => {
        setIsNotifying(true);
        const toastId = toast.loading("Envoi vers Yousign en cours...");

        try {
            const { data: { session } } = await supabase.auth.getSession();
            
            const response = await fetch(`/api/cspe/admin/accounts/${accountId}/send-yousign-cerfa`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session?.access_token}`   // ← virgule supprimée ici
                },                                                        // ← accolade fermante headers
                body: JSON.stringify({ fiscal_year: selectedYear })       // ← body au bon niveau
            });

            const result = await response.json();
            if (!response.ok) throw new Error(result.message || "Erreur serveur Yousign");
            
            toast.success(
                <div>
                    <span className="font-bold">Succès !</span><br/>
                    <span className="text-sm">Le client a reçu l'email de signature.</span>
                </div>, 
                { id: toastId, duration: 5000 }
            );
            
            await fetchClientData(); // Mise à jour du statut

        } catch (error) {
            console.error("Erreur Yousign:", error);
            toast.error(`Erreur : ${error.message}`, { id: toastId });
        } finally {
            setIsNotifying(false);
        }
    };

    // 3. Le Toast de Confirmation (UI Pro)
    toast.custom((t) => (
      <div className={`${t.visible ? 'animate-enter' : 'animate-leave'} max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`}>
        <div className="flex-1 w-0 p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0 pt-0.5">
              {/* Icône Violette pour la signature */}
              <FiPenTool className="h-10 w-10 text-purple-500 rounded-full bg-purple-100 p-2" />
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-gray-900">
                Lancer la signature ?
              </p>
              <p className="mt-1 text-sm text-gray-500">
                Vous allez envoyer le dossier CSPE complet par email à <strong>{client.company_name}</strong> via Yousign.
              </p>
            </div>
          </div>
        </div>
        <div className="flex border-l border-gray-200">
          <button
            onClick={() => {
              toast.dismiss(t.id);
              executeSending(); // Lancer l'action
            }}
            className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-purple-600 hover:text-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            Envoyer
          </button>
          <button
            onClick={() => toast.dismiss(t.id)}
            className="w-full border-t border-gray-200 rounded-none rounded-br-lg p-4 flex items-center justify-center text-sm font-medium text-gray-600 hover:text-gray-500 focus:outline-none"
          >
            Annuler
          </button>
        </div>
      </div>
    ), { duration: Infinity, position: 'top-center' });
  };

  const handleRetrySignature = () => {
    if (!client || !dossierYear?.cerfa_bundle_storage_path) {
        toast.error("Le dossier PDF combiné doit être généré avant la relance.");
        return;
    }

    const executeRetry = async () => {
        setIsNotifying(true);
        const toastId = toast.loading("Relance Yousign en cours...");

        try {
            const { data: { session } } = await supabase.auth.getSession();

            const response = await fetch(`/api/cspe/admin/accounts/${accountId}/send-yousign-cerfa`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session?.access_token}`
                },
                body: JSON.stringify({ fiscal_year: selectedYear })
            });

            const result = await response.json();
            if (!response.ok) throw new Error(result.message || "Erreur serveur Yousign");

            toast.success(
                <div>
                    <span className="font-bold">Relance envoyée !</span><br/>
                    <span className="text-sm">Le client va recevoir un nouvel email de signature.</span>
                </div>,
                { id: toastId, duration: 5000 }
            );

            await fetchClientData();

        } catch (error) {
            console.error("Erreur relance Yousign:", error);
            toast.error(`Erreur : ${error.message}`, { id: toastId });
        } finally {
            setIsNotifying(false);
        }
    };

    toast.custom((t) => (
      <div
        className={`${
          t.visible ? 'animate-enter' : 'animate-leave'
        } max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`}
      >
        <div className="flex-1 w-0 p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0 pt-0.5">
              <FiRefreshCw className="h-10 w-10 text-yellow-500 rounded-full bg-yellow-100 p-2" />
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-gray-900">
                Relancer la signature ?
              </p>
              <p className="mt-1 text-sm text-gray-500">
                Cela <strong>annulera la demande actuelle</strong> (le lien du client ne fonctionnera plus) et enverra un nouvel email.
              </p>
            </div>
          </div>
        </div>
        <div className="flex border-l border-gray-200">
          <button
            onClick={() => {
              toast.dismiss(t.id);
              executeRetry(); // Appel direct sans second toast de confirmation
            }}
            className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-yellow-600 hover:text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
          >
            Oui, relancer
          </button>
          <button
            onClick={() => toast.dismiss(t.id)}
            className="w-full border-t border-gray-200 rounded-none rounded-br-lg p-4 flex items-center justify-center text-sm font-medium text-gray-600 hover:text-gray-500 focus:outline-none"
          >
            Annuler
          </button>
        </div>
      </div>
    ), {
      duration: Infinity,
      position: 'top-center'
    });
  };

  const handleCheckStatus = async () => {
    const toastId = toast.loading("Vérification auprès de Yousign...");
    try {
        const { data: { session } } = await supabase.auth.getSession();
        
        // Notez bien le GET ici
        const response = await fetch(
            `/api/cspe/admin/accounts/${accountId}/check-yousign-status?fiscal_year=${selectedYear}`, // ✅
            {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${session.access_token}` }
            }
        );

        const result = await response.json();
        
        if (result.success) {
            if (result.status === 'done') {
                toast.success("C'est signé ! Dossier mis à jour.", { id: toastId });
                fetchClientData(); // Rafraîchir l'écran pour passer à l'étape verte
            } else {
                toast("Statut actuel : " + result.status, { id: toastId, icon: '⏳' });
            }
        } else {
            throw new Error(result.message);
        }
    } catch (error) {
        toast.error("Erreur : " + error.message, { id: toastId });
    }
};

  // ===== TOUS VOS useEffect CONSERVÉS =====
  
  useEffect(() => {
    if (accountId) { // ✅ CORRIGÉ
      fetchClientData();
    } else {
      loadGlobalData();
    }
  }, [accountId, selectedYear, fetchClientData, loadGlobalData, refreshTrigger]); // ✅ CORRIGÉ

  useEffect(() => {
    if (!accountId) return; // ✅ CORRIGÉ
    const channel = supabase
      .channel(`public:accounts:id=eq.${accountId}`) // 🟣 Changé de profiles à accounts
      .on('postgres_changes', 
        { 
          event: 'UPDATE', 
          schema: 'public', 
          table: 'accounts', // 🟣 Changé de profiles à accounts
          filter: `id=eq.${accountId}` // 🟣 Dans accounts, le filtre est sur 'id'
        }, 
        (payload) => {
          setClient(prevClient => ({ ...prevClient, ...payload.new }));
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [accountId]);

  // Realtime : écouter cspe_dossier_years pour l'année courante
  useEffect(() => {
    if (!accountId || !selectedYear) return;
    const dyChannel = supabase
      .channel(`cspe_dossier_years:${accountId}:${selectedYear}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'cspe_dossier_years',
        filter: `account_id=eq.${accountId}`,
      }, (payload) => {
        if (payload.new && payload.new.fiscal_year === selectedYear) {
          setDossierYear(payload.new);
        }
      })
      .subscribe();

    return () => { supabase.removeChannel(dyChannel); };
  }, [accountId, selectedYear]);

  useEffect(() => {
    if (!accountId) return;
    const docChannel = supabase
      .channel(`public:user_uploaded_documents:account_id=eq.${accountId}`)
      .on('postgres_changes', { 
          event: '*', 
          schema: 'public', 
          table: 'user_uploaded_documents', 
          filter: `account_id=eq.${accountId}` 
      }, 
        () => {
          fetchClientData();
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(docChannel); };
  }, [accountId, fetchClientData]);

  useEffect(() => {
    if (client && client.account_id) {
      fetchGeneratedCerfas();
      fetchElectroIntensite();
    }
  }, [client, fetchGeneratedCerfas, fetchElectroIntensite]);

  // ===== CHARGEMENT DES LIGNES MANUELLES PAR PDL (pour mode manuel) =====
  const fetchManualLinesCounts = useCallback(async () => {
    if (!accountId || !selectedYear) return;
    try {
      const { data, error } = await supabase
        .from('ticfe_manual_lines')
        .select('pdl_id')
        .eq('account_id', accountId)
        .eq('year', selectedYear);

      if (error) throw error;

      const counts = (data || []).reduce((acc, row) => {
        if (row.pdl_id) acc[row.pdl_id] = (acc[row.pdl_id] || 0) + 1;
        return acc;
      }, {});
      setManualLinesCountByPdl(counts);
    } catch (err) {
      console.error('[AnalyseCspe] Erreur chargement lignes manuelles:', err);
    }
  }, [accountId, selectedYear]);

  // ===== CHARGEMENT DES FLAGS MODE MANUEL =====
  const fetchManualModeFlags = useCallback(async () => {
    if (!accountId || !selectedYear) return;
    try {
      const { data, error } = await supabase
        .from('cspe_manual_mode')
        .select('pdl_id, enabled')
        .eq('account_id', accountId)
        .eq('year', selectedYear);

      if (error) throw error;

      const flags = (data || []).reduce((acc, row) => {
        if (row.pdl_id && row.enabled) acc[row.pdl_id] = true;
        return acc;
      }, {});
      setManualModeFlags(flags);
    } catch (err) {
      console.error('[AnalyseCspe] Erreur chargement mode manuel:', err);
    }
  }, [accountId, selectedYear]);

  useEffect(() => {
    if (accountId && selectedYear) {
      fetchManualLinesCounts();
      fetchManualModeFlags();
    }
  }, [accountId, selectedYear, fetchManualLinesCounts, fetchManualModeFlags]);

  // ===== TOUTES VOS LOGIQUES CONDITIONNELLES CONSERVÉES =====
  
  // Au moins un ERA généré (pour le workflow step)
  const cerfaEraGenerePourExercice = useMemo(() => {
    if (!selectedYear || !generatedCerfas) return false;
    return generatedCerfas.some(cf => cf.document_type === 'CERFA_ERA');
  }, [selectedYear, generatedCerfas]);
  
  const cerfaTicVaGenerePourExercice = useMemo(() => {
    if (!selectedYear || !generatedCerfas) return false;
    return generatedCerfas.some(cf => cf.document_type === 'CERFA_TIC_VA');
  }, [selectedYear, generatedCerfas]);
  
  const cerfa3310TicGenerePourPeriode = useMemo(() => {
    if (!selectedYear || !generatedCerfas) return false;
    return generatedCerfas.some(cf => cf.document_type === 'CERFA_3310_TIC');
  }, [selectedYear, generatedCerfas]);

  const allIndividualCerfasGenerated = cerfaEraGenerePourExercice && cerfaTicVaGenerePourExercice && cerfa3310TicGenerePourPeriode;

  const liasseOcrTraitee = fiscalData != null && fiscalData.cvae_reference_turnover != null;
  
  const canFinalizeVerification = useMemo(() => {
    if (!client) return false;
    return client.dossier_stage === 'verification' && !client.admin_docs_verified_at && allIndividualCerfasGenerated;
  }, [client, allIndividualCerfasGenerated]);

  const groupedDocuments = useMemo(() => {
    const groups = { 'documents_financiers': [] };
    (allUserDocuments || []).forEach(doc => {
      if (
        doc.document_type_id === 'documents_financiers' &&
        Number(doc.year) === selectedYear          // ✅ filtre sur l'année courante
      ) {
        groups['documents_financiers'].push(doc);
      }
    });
    return groups;
  }, [allUserDocuments, selectedYear]);

  const isLiasseValidated = useMemo(() => {
    const financialDocs = groupedDocuments['documents_financiers'] || [];
    if (financialDocs.length === 0) return false;
    return financialDocs.some(doc => 
      doc.status === 'Validé manuellement' || doc.status === 'Validé'
    );
  }, [groupedDocuments]);

  const invoicesCountByPdlYear = useMemo(() => {
  const map = new Map();

  (allUserDocuments || []).forEach((doc) => {
    const isElecInvoice =
      doc.document_type_id === 'factures_electricite' ||
      doc.document_type_id === 'facture_electricite';

    if (!isElecInvoice) return;
    if (!doc.pdl_id || !doc.year) return;

    const key = `${doc.pdl_id}:${doc.year}`;
    map.set(key, (map.get(key) || 0) + 1);
  });

  return map;
}, [allUserDocuments]);

const hasInvoicesForPdlYear = useCallback((pdlId, year) => {
  return (invoicesCountByPdlYear.get(`${pdlId}:${year}`) || 0) > 0;
}, [invoicesCountByPdlYear]);

const hasManualLinesForPdl = useCallback((pdlId) => {
  return (manualLinesCountByPdl[pdlId] || 0) > 0;
}, [manualLinesCountByPdl]);

  // Nouvel ordre : Liasse → TIC VA → ERA → 3310
  // Intensité validée : null = VA négative (réputée > 6,75% depuis 2025 → OK), >= 0.5 → OK
  const intensiteValidee = electroIntensite === null || (typeof electroIntensite === 'number' && electroIntensite >= 0.5);
  const intensiteInsuffisante = cerfaTicVaGenerePourExercice && !intensiteValidee;

  // Tous les PDLs avec données doivent avoir leur ERA
  const tousEraGeneres = useMemo(() => {
    if (!selectedYear || !generatedCerfas || !pdlList || pdlList.length === 0) return false;
    const eraCerfas = generatedCerfas.filter(cf => cf.document_type === 'CERFA_ERA');
    const pdlsAvecData = pdlList.filter(pdl => {
      const hasInvoices = (invoicesCountByPdlYear.get(`${pdl.id}:${selectedYear}`) || 0) > 0;
      const hasManual = (manualLinesCountByPdl[pdl.id] || 0) > 0;
      return hasInvoices || hasManual;
    });
    if (pdlsAvecData.length === 0) return false;
    return pdlsAvecData.every(pdl => eraCerfas.some(cf => cf.pdl_id === pdl.id));
  }, [selectedYear, generatedCerfas, pdlList, invoicesCountByPdlYear, manualLinesCountByPdl]);

  const disableGenerateTicVaButton = isGeneratingTICVA || cerfaTicVaGenerePourExercice || !isLiasseValidated || !fiscalData?.id;
  // Règles d'activation ERA / 3310 : cspeGating.js (testées). Un compte sans PDL exonéré
  // garde exactement les anciennes conditions (test de non-régression dans cspeGating.test.js).
  const pdlsAvecDataPourAnnee = useMemo(
    () => (pdlList || []).filter((p) => hasInvoicesForPdlYear(p.id, selectedYear) || hasManualLinesForPdl(p.id)),
    [pdlList, selectedYear, hasInvoicesForPdlYear, hasManualLinesForPdl]
  );
  const disableGenerate3310TicButton = !peutGenerer3310({
    pdlsAvecData: pdlsAvecDataPourAnnee,
    tousEraGeneres,
    enCours: isGenerating3310TIC,
    dejaGenere: cerfa3310TicGenerePourPeriode,
    intensiteInsuffisante,
  });

  const calculatedOverallDossierStatus = useMemo(() => {
    if (loading) return { text: "Chargement...", color: "bg-slate-100 text-slate-700", icon: <FiLoader className="animate-spin"/> };
    if (!client) return { text: "N/A", color: "bg-slate-100 text-slate-700" };
    const status = getCategoryStatus(groupedDocuments['documents_financiers'] || []).text;
    if (status === 'Action Requise' || status === 'À fournir') return { text: "Incomplet", color: "bg-orange-100 text-orange-800", icon: <FiAlertTriangle /> };
    if (status === 'Complet') return { text: "Complet", color: "bg-green-100 text-green-800", icon: <FiCheckSquare /> };
    return { text: "En Revue", color: "bg-yellow-100 text-yellow-800", icon: <FiClock /> };
  }, [groupedDocuments, client, loading]);

  // ===== GESTION DES ÉTATS DE CHARGEMENT =====
  const filteredClients = useMemo(() => {
    if (!clientSearch) {
      return clientsWithPdls;
    }
    const lowercasedFilter = clientSearch.toLowerCase();
    return clientsWithPdls.filter(client => {
      const nameMatch = client.user_name && client.user_name.toLowerCase().includes(lowercasedFilter);
      const companyMatch = client.company_name && client.company_name.toLowerCase().includes(lowercasedFilter);
      return nameMatch || companyMatch;
    });
  }, [clientSearch, clientsWithPdls]);

  // Nouvel ordre : 0=Liasse → 1=TIC VA → 2=ERA → 3=3310 → 4=Finalisation → 5=Combinaison → 6=Signature
  const currentWorkflowStep = useMemo(() => {
    if (!dossierYear) {
      if (cerfa3310TicGenerePourPeriode)                return 4;
      if (cerfaEraGenerePourExercice)                   return 3;
      if (cerfaTicVaGenerePourExercice)                 return 2;
      if (liasseOcrTraitee && isLiasseValidated)        return 1;
      return 0;
    }
    if (dossierYear.cerfa_bundle_generated_at)           return 6;
    if (dossierYear.admin_cerfa_generation_completed_at) return 5;
    if (cerfa3310TicGenerePourPeriode)                   return 4;
    if (cerfaEraGenerePourExercice)                      return 3;
    if (cerfaTicVaGenerePourExercice)                    return 2;
    if (liasseOcrTraitee && isLiasseValidated)           return 1;
    return 0;
  }, [
    dossierYear,
    cerfaEraGenerePourExercice,
    liasseOcrTraitee,
    isLiasseValidated,
    cerfaTicVaGenerePourExercice,
    cerfa3310TicGenerePourPeriode,
  ]);

  // ✅ Reset quand on change RÉELLEMENT de client ou d'année
    const prevAccountIdRef = useRef(accountId);
    const prevSelectedYearRef = useRef(selectedYear);
    useEffect(() => {
      if (!accountId) return;
      const accountChanged = prevAccountIdRef.current !== accountId;
      const yearChanged = prevSelectedYearRef.current !== selectedYear;
      prevAccountIdRef.current = accountId;
      prevSelectedYearRef.current = selectedYear;
      if (accountChanged || yearChanged) {
        prevWorkflowStepRef.current = null;
        setOpenStepIndex(0);
      }
    }, [accountId, selectedYear]);

    // ✅ 1) À l’arrivée sur un client : ouvrir Étape 1
    // ✅ 2) Ensuite : auto-advance uniquement si on progresse (currentWorkflowStep augmente)
    // ✅ 3) Mode manuel (pas d’analysisResult) : forcer étape 1 (index 0) et bloquer l’auto-advance
    const arrivedFromManualMode = !location.state?.analysisResult;
    const manualModeLockRef = useRef(arrivedFromManualMode);
    useEffect(() => {
      if (!accountId) return; // seulement en vue détail

      // Première arrivée sur la fiche (ou après reset année/client)
      if (prevWorkflowStepRef.current === null) {
        prevWorkflowStepRef.current = currentWorkflowStep;
        // Mode manuel : toujours ouvrir l’étape 1 pour saisir les lignes
        if (manualModeLockRef.current) {
          setOpenStepIndex(0);
        } else {
          setOpenStepIndex(currentWorkflowStep);
        }
        return;
      }

      // Auto-advance : uniquement si on progresse ET pas en mode manuel lock
      if (!manualModeLockRef.current && currentWorkflowStep > prevWorkflowStepRef.current) {
        setOpenStepIndex(currentWorkflowStep);
      }

      prevWorkflowStepRef.current = currentWorkflowStep;
    }, [accountId, currentWorkflowStep]);

  const stats = useMemo(() => {
  const total = clientsWithPdls.length;
  
  const accountsWithAnalysis = new Set(allAnalyses.map(a => String(a.account_id)));
  const enCours = accountsWithAnalysis.size;

  const complets = clientsWithPdls.filter(c => 
    ['dossier_pret_signature', 'en_attente_signature', 'signe', 'ticva_genere'].includes(c.dossier_stage)
  ).length;

  const signes = clientsWithPdls.filter(c => c.cerfa_signed_at).length;

  return { total, enCours, complets, signes };
}, [clientsWithPdls, allAnalyses]);


  const isGlobalView = !accountId;

// Map account_id -> contrat taxe signé (pour afficher date + doc)
const contractByAccountId = useMemo(() => {
  const map = new Map();
  for (const c of signedTaxContracts) {
    const id = c?.account_id ? String(c.account_id) : null;
    if (id && !map.has(id)) map.set(id, c);
  }
  return map;
}, [signedTaxContracts]);

// Clients filtrés + triés (ne calcule réellement que sur la vue globale)
const filteredAndSortedClients = useMemo(() => {
  if (!isGlobalView) return [];

  let data = [...clientsWithPdls];

  // search
  if (filters.search) {
    const q = filters.search.toLowerCase();
    data = data.filter((c) => {
      const aId = String(c.account_id || "");
      const contract = contractByAccountId.get(aId);
      const companyFromContract = contract?.account?.company_name || "";
      const company = (c.company_name || companyFromContract || "").toLowerCase();
      const userName = (c.user_name || "").toLowerCase();
      return company.includes(q) || userName.includes(q) || aId.includes(q);
    });
  }

  // (optionnel) filtre status
  if (filters.status === "has_analysis") {
    const accountsWithAnalysis = new Set(allAnalyses.map((a) => String(a.account_id)));
    data = data.filter((c) => accountsWithAnalysis.has(String(c.account_id)));
  } else if (filters.status === "no_analysis") {
    const accountsWithAnalysis = new Set(allAnalyses.map((a) => String(a.account_id)));
    data = data.filter((c) => !accountsWithAnalysis.has(String(c.account_id)));
  }

  // tri
  data.sort((a, b) => {
    const aId = String(a.account_id || "");
    const bId = String(b.account_id || "");
    const ca = contractByAccountId.get(aId);
    const cb = contractByAccountId.get(bId);

    const getVal = (row, contract) => {
      if (sortConfig.key === "company_name") return row.company_name || contract?.account?.company_name || "";
      if (sortConfig.key === "signed_at") return contract?.signed_at || "";
      if (sortConfig.key === "pdl_count") return row.pdl_count || (row.pdls?.length ?? 0);
      return row?.[sortConfig.key] ?? "";
    };

    const av = getVal(a, ca);
    const bv = getVal(b, cb);

    if (sortConfig.key === "signed_at") {
      const ta = av ? new Date(av).getTime() : 0;
      const tb = bv ? new Date(bv).getTime() : 0;
      return sortConfig.direction === "asc" ? ta - tb : tb - ta;
    }

    if (sortConfig.key === "pdl_count") {
      const na = Number(av) || 0;
      const nb = Number(bv) || 0;
      return sortConfig.direction === "asc" ? na - nb : nb - na;
    }

    const cmp = String(av).localeCompare(String(bv), "fr", { numeric: true, sensitivity: "base" });
    return sortConfig.direction === "asc" ? cmp : -cmp;
  });

  return data;
}, [
  isGlobalView,
  clientsWithPdls,
  filters.search,
  filters.status,
  sortConfig,
  contractByAccountId,
  allAnalyses,
]);

const activeFiltersCount = useMemo(
  () => Object.values(filters).filter(Boolean).length,
  [filters]
);

const period = useMemo(
  () => periodLabel(selectedYear, selectedMonth),
  [selectedYear, selectedMonth]
);

 if (loading) {
    return (
      <div className="h-screen flex justify-center items-center">
        <div className="text-center space-y-3">
          <FiLoader className="animate-spin h-8 w-8 text-orange-600 mx-auto" />
          <p className="text-gray-600 text-sm font-medium">Chargement des analyses CSPE...</p>
        </div>
      </div>
    );
  }

  // ===== VUE GLOBALE COMPACTE (page d'accueil) =====
  if (!accountId) {
    return (
      <div className="min-h-screen bg-gray-50 p-3">
        <div className="max-w-6xl mx-auto space-y-2">

          {/* Sélecteurs période */}
          <div className="flex items-center gap-2 justify-end">
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value, 10))}
              className="py-1 px-1.5 border border-gray-200 rounded text-[10px] bg-white"
            >
              <option value={0}>Tous les mois</option>
              {Array.from({ length: 12 }).map((_, i) => (
                <option key={i + 1} value={i + 1}>
                  {MOIS[i]}
                </option>
              ))}
            </select>

            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
              className="py-1 px-1.5 border border-gray-200 rounded text-[10px] bg-white"
            >
              {[2023, 2024, 2025, 2026, 2027].map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>

          {/* Table des contrats signés — autonome, sans loadGlobalData */}
          <SignedContractsCSPE
            selectedYear={selectedYear}
            selectedMonth={selectedMonth}
            session={session}
            onSelectClient={(contract) => {
              navigate(`/dashboard/admin/analyse-cspe/${contract.account_id}`, {
                state: { year: selectedYear, month: selectedMonth },
              });
            }}
          />

        </div>
      </div>
    );
  }

  // ===== VUE DÉTAILLÉE COMPACTE (un seul client) =====
  if (!client) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center bg-white rounded-xl p-8 shadow-lg">
          <FiBarChart2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-gray-900 mb-3">Client non trouvé</h3>
          <p className="text-gray-600 text-sm">
            Aucune donnée disponible pour ce client.
          </p>
          <Link 
            to="/dashboard/admin/analyse-cspe"
            className="inline-flex items-center mt-4 px-4 py-2 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700 transition-colors text-sm"
          >
            <FiArrowLeft className="h-4 w-4 mr-1.5" />
            Retour à la liste
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header ultra compact */}
      <CompactHeader
        client={client}
        loading={loading}
        onRefresh={() => fetchClientData()}
        selectedYear={selectedYear}
        onEditRaisonSociale={() => setIsRaisonSocialeModalOpen(true)}
      />

      {/* Système d'onglets compact */}
      <TabSystem 
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {/* Messages de succès/erreur compacts */}
      {success && (
        <div className="bg-green-50 p-2 mx-2 mt-2 rounded text-xs">
          <p className="text-green-800">{success}</p>
        </div>
      )}
      {errorMessage && (
        <div className="bg-red-50 p-2 mx-2 mt-2 rounded text-xs">
          <p className="text-red-800">{errorMessage}</p>
        </div>
      )}

      {/* Contenu des onglets */}
      <div className="flex-1 overflow-hidden bg-white">
        {activeTab === 'analyse' && (
          <div className="h-full overflow-y-auto p-2 space-y-2">

            {/* année de travail (XS + sélection) */}
          <div className="bg-white rounded-md shadow-sm border border-gray-200 p-2">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-semibold text-slate-700">
                Année fiscale de travail
              </p>

              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
                className="px-2 py-1 bg-orange-50 text-orange-800 text-xs font-bold rounded-md border border-orange-200"
                title="Année fiscale de travail"
              >
                {[2023, 2024, 2025, 2026, 2027].map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>


            {/* 1. Import/OCR/Validation Liasse Fiscale */}
            <WorkflowStepSection
              stepIndex={0}
              title="Étape 1 : Import et Traitement Liasse Fiscale"
              subtitle="Upload, OCR et validation des données fiscales."
              icon={FiUpload}
              isActive={openStepIndex === 0}
              isOpen={openStepIndex === 0}
              onToggle={() => setOpenStepIndex(openStepIndex === 0 ? null : 0)}
            >
              <DocumentCategorySection
                key="documents_financiers"
                categoryKey="documents_financiers"
                categoryName="Documents financiers (Liasse fiscale)"
                documents={groupedDocuments['documents_financiers'] || []}
                onValidateAllInCategory={handleValidateAllInCategory}
                onProcessLiasse={handleProcessLiasse}
              >
                {(groupedDocuments['documents_financiers'] || []).length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200">
                      <tbody className="bg-white divide-y divide-slate-200">
                        {(groupedDocuments['documents_financiers'] || []).map(doc => (
                        <tr key={doc.id}>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <div className="text-xs font-medium text-slate-900">{doc.original_file_name}</div>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${STATUS_DISPLAY[doc.status]?.color || 'bg-gray-100'}`}>
                              {doc.status}
                            </span>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-medium">
                            <div className="flex items-center justify-end gap-2">
                              <DocumentActionButtons
                                doc={doc}
                                onValidate={handleValidateDocument}
                                onReject={handleRejectDocument}
                                onProcessLiasse={handleProcessLiasse}
                                isProcessingLiasse={isProcessingLiasse}
                              />
                              <button
                                onClick={() => handleDeleteLiasse(doc)}
                                className="p-1.5 rounded text-red-500 hover:text-red-700 hover:bg-red-50 transition-colors"
                                title="Supprimer cette liasse"
                              >
                                <FiTrash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-4">
                    <FileUploadZone onFileUpload={handleLiasseUpload} />
                  </div>
                )}
              </DocumentCategorySection>
            </WorkflowStepSection>

            {/* 2. CERFA TIC-VA */}
            <WorkflowStepSection
              stepIndex={1}
              title="Étape 2 : Calcul TIC-VA (Électro-intensité)"
              subtitle="Génération du CERFA 2040-TIC-VA-E-SD."
              icon={FiDownloadCloud}
              isActive={openStepIndex === 1}
              isOpen={openStepIndex === 1}
              onToggle={() => setOpenStepIndex(openStepIndex === 1 ? null : 1)}
            >
              <div className="bg-sky-50 border-l-4 border-sky-400 p-2 mb-3">
                <p className="text-xs text-sky-700">
                  <FiAlertTriangle className="inline h-3 w-3 mr-1 align-text-top" />
                  Nécessite que les données de la liasse fiscale aient été extraites et validées.
                </p>
              </div>

              {!liasseOcrTraitee && (
                <div className="bg-orange-50 border-l-4 border-orange-400 p-2 mb-3">
                  <p className="text-xs text-orange-700">
                    L'analyse (OCR) de la liasse fiscale doit être effectuée avant de générer le CERFA TIC-VA.
                  </p>
                </div>
              )}

              <div className="flex items-center justify-between">
                <button
                    onClick={handleGenerateCerfaTICVA}
                    disabled={disableGenerateTicVaButton}
                    className={`w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded shadow-sm text-white ${disableGenerateTicVaButton ? 'bg-slate-400 cursor-not-allowed' : 'bg-teal-600 hover:bg-teal-700'}`}
                    title={disableGenerateTicVaButton ? "Prérequis manquants ou CERFA déjà généré." : "Générer le CERFA pour la Valeur Ajoutée"}
                >
                  {isGeneratingTICVA ? <FiLoader className="animate-spin h-4 w-4 mr-1.5" /> : <FiFileText className="h-4 w-4 mr-1.5" />}
                  {isGeneratingTICVA ? 'Génération...' : cerfaTicVaGenerePourExercice ? 'CERFA (TIC-VA) Généré' : 'Générer CERFA (TIC-VA)'}
                </button>

                {cerfaTicVaGenerePourExercice && (
                  <div className={`ml-4 px-4 py-2 border-2 rounded-lg text-center ${
                    intensiteValidee
                      ? 'bg-green-50 border-green-300'
                      : 'bg-red-50 border-red-300'
                  }`}>
                    <div className={`text-[10px] uppercase tracking-wide font-medium ${intensiteValidee ? 'text-green-600' : 'text-red-600'}`}>Électro-intensité</div>
                    <div className={`text-xl font-bold ${intensiteValidee ? 'text-green-700' : 'text-red-700'}`}>
                      {electroIntensite === null ? '> 6,75 %' : typeof electroIntensite === 'number' ? `${electroIntensite.toFixed(2)} %` : '—'}
                    </div>
                    {!intensiteValidee && (
                      <div className="text-[10px] text-red-500 mt-0.5">Seuil requis : 0,50 %</div>
                    )}
                  </div>
                )}
              </div>
            </WorkflowStepSection>

            {/* 3. CERFA ERA */}
            <WorkflowStepSection
              stepIndex={2}
              title="Étape 3 : Génération des CERFA ERA (par PDL)"
              subtitle="Générez un document pour chaque Point de Livraison."
              icon={FiFileText}
              isActive={openStepIndex === 2}
              isOpen={openStepIndex === 2}
              onToggle={() => setOpenStepIndex(openStepIndex === 2 ? null : 2)}
            >
              {afficherBandeauPrerequisEra({ liasseValidee: isLiasseValidated, ticVaGenere: cerfaTicVaGenerePourExercice, pdlsAvecData: pdlsAvecDataPourAnnee }) && (
                <div className="bg-orange-50 border-l-4 border-orange-400 p-2 mb-3">
                  <p className="text-xs text-orange-700">
                    <FiAlertTriangle className="inline h-3 w-3 mr-1 align-text-top" />
                    Les étapes 1 (Liasse fiscale) et 2 (TIC-VA) doivent être complétées avant de générer les ERA.
                  </p>
                </div>
              )}

              <div className="space-y-2">
                {pdlList.length > 0 ? (
                  pdlList.map((pdl) => {
                  const hasDocsForYear = hasInvoicesForPdlYear(pdl.id, selectedYear);
                  const hasManualLines = hasManualLinesForPdl(pdl.id);
                  const isManualMode = !!manualModeFlags[pdl.id];
                  const canGenerate = peutGenererEra({
                    pdl,
                    aDesDonnees: hasDocsForYear || hasManualLines,
                    enCours: generatingPdlId !== null,
                    liasseValidee: isLiasseValidated,
                    ticVaGenere: cerfaTicVaGenerePourExercice,
                    intensiteInsuffisante,
                  });
                  const exoLibelle = libelleExoneration(pdl);

                  const isCerfaGenerated = generatedCerfas.some((cerfa) => {
                    if (!cerfa.pdl_id || !cerfa.file_name) return false;
                    const pdlMatch = cerfa.pdl_id === pdl.id;
                    const yearFromName = cerfa.file_name.match(/_(\d{4})(?:_|\.pdf)/)?.[1];
                    const yearMatch = yearFromName ? parseInt(yearFromName, 10) === selectedYear : false;
                    return pdlMatch && yearMatch;
                  });
                        return (
                        <div key={pdl.id} data-testid={`pdl-row-${pdl.pdl_number}`} className={`p-2 rounded-md border ${isManualMode && !hasDocsForYear ? 'bg-purple-50 border-purple-200' : 'bg-slate-50'}`}>
                            <div className="flex items-center justify-between">
                            <div className="flex items-center flex-wrap gap-1">
                                <span className="font-medium text-gray-800 text-xs">{pdl.site_name || 'Site inconnu'}</span>
                                {pdl.pdl_number && (
                                  <span className="ml-1 text-[10px] font-mono text-gray-500">{pdl.pdl_number}</span>
                                )}
                                {exoLibelle && (
                                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-800" data-testid={`badge-exo-${pdl.pdl_number}`}>
                                    <FiShield className="mr-1 h-3 w-3" /> Exonéré {exoLibelle}
                                  </span>
                                )}
                                {isCerfaGenerated ? (
                                <span className="ml-3 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  <FiCheck className="mr-1 h-3 w-3" /> Généré
                                </span>
                              ) : hasDocsForYear ? (
                                <span className="ml-3 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                  <FiClock className="mr-1 h-3 w-3" /> À générer
                                </span>
                              ) : hasManualLines ? (
                                <span className="ml-3 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                  <FiEdit3 className="mr-1 h-3 w-3" /> {manualLinesCountByPdl[pdl.id]} ligne(s) manuelle(s)
                                </span>
                              ) : isManualMode ? (
                                <span className="ml-3 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                  <FiEdit3 className="mr-1 h-3 w-3" /> Mode manuel — cliquez sur le crayon pour saisir
                                </span>
                              ) : (
                                <span className="ml-3 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-200 text-slate-700">
                                  <FiX className="mr-1 h-3 w-3" /> Aucune facture {selectedYear}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => openExoEditor(pdl)}
                                className={`p-2 rounded hover:bg-slate-200/60 transition-colors ${pdl.exoneration_motif ? 'text-emerald-600 hover:text-emerald-800' : 'text-slate-400 hover:text-slate-700'}`}
                                title={pdl.exoneration_motif ? `Exonération : ${exoLibelle}` : "Configurer une exonération d'accise (E04…)"}
                                aria-label="Configurer l'exonération d'accise"
                                data-testid={`btn-exo-${pdl.pdl_number}`}
                              >
                                <FiShield className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => openSiretEditor(pdl)}
                                className={`p-2 rounded hover:bg-slate-200/60 transition-colors ${
                                  pdl.siret_etablissement
                                    ? 'text-slate-600 hover:text-slate-900'
                                    : 'text-orange-500 hover:text-orange-700'
                                }`}
                                title={
                                  pdl.siret_etablissement
                                    ? `SIRET établissement : ${pdl.siret_etablissement}${pdl.naf_code ? ' · APE ' + pdl.naf_code : ''}`
                                    : 'Définir le SIRET de l\'établissement (utilisera le siège par défaut)'
                                }
                                aria-label="Éditer SIRET établissement et code APE"
                              >
                                <FiSettings className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => openTicfeEditor(pdl)}
                                className="p-2 rounded hover:bg-slate-200/60 text-slate-600 hover:text-slate-900 transition-colors"
                                title={hasManualLines ? 'Modifier les lignes manuelles' : 'Corriger les champs factures (OCR) ou saisir manuellement'}
                                aria-label="Corriger les champs factures ou saisir manuellement"
                              >
                                <FiEdit3 className="h-4 w-4" />
                              </button>
                              {isCerfaGenerated ? (
                                <span className="text-xs font-medium text-gray-500">
                                  Déjà traité pour {selectedYear}
                                </span>
                              ) : !hasDocsForYear && !hasManualLines ? (
                                <button
                                  onClick={() => navigate(`/dashboard/admin/client/${client?.id || accountId}`, { state: { activeTab: 'taxes' } })}
                                  className="inline-flex items-center justify-center px-3 py-1 text-xs font-medium rounded text-white shadow-sm bg-orange-500 hover:bg-orange-600"
                                  title={`Ajouter des factures pour ${pdl.site_name}`}
                                >
                                  <FiUpload className="h-3 w-3 mr-1.5" />
                                  Ajouter factures
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleGenerateCerfaERA(pdl)}
                                  disabled={!canGenerate}
                                  className={`inline-flex items-center justify-center px-3 py-1 text-xs font-medium rounded text-white shadow-sm ${
                                    !canGenerate ? 'bg-slate-300 cursor-not-allowed' : hasManualLines && !hasDocsForYear ? 'bg-purple-600 hover:bg-purple-700' : 'bg-blue-600 hover:bg-blue-700'
                                  }`}
                                  title={
                                    !pdl.exoneration_motif && (!isLiasseValidated || !cerfaTicVaGenerePourExercice)
                                      ? 'Complétez les étapes 1 et 2 d\'abord'
                                      : `Générer le CERFA pour ${pdl.site_name}`
                                  }
                                >
                                  {generatingPdlId === pdl.id ? (
                                    <FiLoader className="animate-spin h-3 w-3 mr-1.5" />
                                  ) : (
                                    <FiCpu className="h-3 w-3 mr-1.5" />
                                  )}
                                  {generatingPdlId === pdl.id ? 'En cours...' : 'Générer'}
                                </button>
                              )}
                            </div>
                            </div>
                           <div className="mt-2 px-1">
                              <button
                                type="button"
                                role="switch"
                                aria-checked={!!premierBilanConfig[`${pdl.id}-${selectedYear}`]?.enabled}
                                onClick={() => {
                                  const key = `${pdl.id}-${selectedYear}`;
                                  const next = !premierBilanConfig[key]?.enabled;
                                  setPremierBilanConfig((prev) => ({
                                    ...prev,
                                    [key]: {
                                      ...prev[key],
                                      enabled: next,
                                      startDate: next ? prev[key]?.startDate || '' : '',
                                    },
                                  }));
                                }}
                                className="flex items-center gap-2 w-fit"
                              >
                                <span
                                  className={`relative inline-flex h-4 w-7 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                                    premierBilanConfig[`${pdl.id}-${selectedYear}`]?.enabled ? 'bg-amber-500' : 'bg-gray-300'
                                  }`}
                                >
                                  <span
                                    className={`pointer-events-none inline-block h-3 w-3 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                      premierBilanConfig[`${pdl.id}-${selectedYear}`]?.enabled ? 'translate-x-3' : 'translate-x-0'
                                    }`}
                                  />
                                </span>
                                <span className={`text-xs font-medium ${premierBilanConfig[`${pdl.id}-${selectedYear}`]?.enabled ? 'text-amber-800' : 'text-gray-500'}`}>
                                  Premier bilan / Exercice atypique
                                </span>
                              </button>

                              {premierBilanConfig[`${pdl.id}-${selectedYear}`]?.enabled && (
                                <div className="mt-1.5 ml-5 flex items-center gap-2">
                                  <span className="text-xs text-amber-700">Début réel :</span>
                                  <input
                                    type="date"
                                    value={premierBilanConfig[`${pdl.id}-${selectedYear}`]?.startDate || ''}
                                    onChange={(e) =>
                                      setPremierBilanConfig((prev) => ({
                                        ...prev,
                                        [`${pdl.id}-${selectedYear}`]: {
                                          ...prev[`${pdl.id}-${selectedYear}`],
                                          startDate: e.target.value,
                                        }
                                      }))
                                    }
                                    className="text-xs border border-amber-300 rounded px-1.5 py-0.5 bg-white"
                                  />
                                </div>
                              )}
                            </div>
                        </div>
                        );
                    })
                    ) : (
                    <p className="text-xs text-slate-500 italic text-center">Aucun PDL n'a été trouvé pour ce client.</p>
                    )}
                </div>
                </WorkflowStepSection>

            {/* 4. CERFA 3310-TIC */}
            <WorkflowStepSection
              stepIndex={3}
              title="Étape 4 : CERFA 3310-TIC"
              subtitle="Génération du CERFA 3310-TIC-SD."
              icon={FiDownloadCloud}
              isActive={openStepIndex === 3}
              isOpen={openStepIndex === 3}
              onToggle={() => setOpenStepIndex(openStepIndex === 3 ? null : 3)}
            >
              <div className="bg-purple-50 border-l-4 border-purple-400 p-2 mb-3">
                <p className="text-xs text-purple-700">
                  <FiAlertTriangle className="inline h-3 w-3 mr-1 align-text-top" />
                  Assurez-vous que les CERFA ERA ont bien été générés pour la période.
                </p>
              </div>
              <button
                  onClick={handleGenerateCerfa3310TIC}
                  disabled={disableGenerate3310TicButton}
                  className={`w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded shadow-sm text-white ${ disableGenerate3310TicButton ? 'bg-slate-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'}`}
              >
                {isGenerating3310TIC ? <FiLoader className="animate-spin h-4 w-4 mr-1.5" /> : <FiDownloadCloud className="h-4 w-4 mr-1.5" />}
                {isGenerating3310TIC ? 'Génération...' : cerfa3310TicGenerePourPeriode ? 'CERFA (3310-TIC) Généré' : 'Générer CERFA (3310-TIC)'}
              </button>
            </WorkflowStepSection>

            {/* 5. Finalisation & Validation */}
            <WorkflowStepSection
              stepIndex={4}
              title="Étape 5 : Finalisation et Validation"
              subtitle="Validation finale et confirmation de la génération."
              icon={FiCheckSquare}
              isActive={openStepIndex === 4}
              isOpen={openStepIndex === 4}
              onToggle={() => setOpenStepIndex(openStepIndex === 4 ? null : 4)}
            >
              <div className="space-y-3">
                {/* Action sur la Vérification des Documents */}
                <div className="p-3 bg-white rounded border border-gray-200 flex justify-between items-center">
                  <div>
                      <h4 className="text-sm font-medium text-slate-700">Action sur la Vérification des Documents</h4>
                      <p className="text-xs text-slate-500">Statut utilisateur: <span className="font-medium">{client.dossier_stage || 'N/A'}</span></p>
                  </div>
                  <button
                        onClick={handleMarkDocsVerified}
                        disabled={
                          isMarkingVerificationComplete || 
                          !['cerfa_genere', 'cerfa_3310_genere', 'dossier_pret_signature'].includes(client.dossier_stage)
                          // ✅ cerfa_genere ajouté
                        }
                        className={`px-3 py-1.5 text-xs font-medium rounded flex items-center ${
                          (isMarkingVerificationComplete || !['cerfa_genere', 'cerfa_3310_genere', 'dossier_pret_signature'].includes(client.dossier_stage))
                          // ✅ même correction ici
                          ? 'bg-gray-300 cursor-not-allowed' 
                          : 'bg-green-600 text-white hover:bg-green-700'
                        }`}
                      >
                    {isMarkingVerificationComplete ? <FiLoader className="animate-spin h-3 w-3 mr-1" /> : <FiCheckSquare className="h-3 w-3 mr-1" />}
                    {client.admin_docs_verified_at ? 'Vérification Terminée' : 'Finaliser Vérification'}
                </button>
                </div>

                {/* Confirmation génération CERFAs */}
                {client && fiscalData && (
                  <div className={`p-3 border rounded ${dossierYear?.admin_cerfa_generation_completed_at ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'}`}>
                    {allIndividualCerfasGenerated ? (
                      !dossierYear?.admin_cerfa_generation_completed_at ? (
                        <div className="space-y-2">
                          <p className="text-xs text-slate-600">Tous les documents sont générés. Confirmez pour passer à l'étape de combinaison.</p>
                          <button onClick={handleConfirmCerfaGeneration} disabled={isConfirmingCerfaGen} className={`w-full sm:w-auto px-4 py-2 text-sm font-medium rounded shadow-sm text-white flex items-center justify-center ${isConfirmingCerfaGen ? 'bg-gray-400' : 'bg-emerald-600 hover:bg-emerald-700'}`}>
                            {isConfirmingCerfaGen ? <FiLoader className="animate-spin h-4 w-4 mr-1.5" /> : <FiCheck className="h-4 w-4 mr-1.5" />}
                            Confirmer Génération Terminée
                          </button>
                        </div>
                      ) : (
                        <p className="text-xs text-green-700 font-medium flex items-center">
                          <FiCheckSquare className="h-4 w-4 mr-1.5" />
                          Génération confirmée le {new Date(dossierYear.admin_cerfa_generation_completed_at).toLocaleDateString('fr-FR')}.
                        </p>
                      )
                    ) : (
                      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3">
                        <p className="text-xs text-yellow-700">En attente de la génération de tous les documents CERFA.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </WorkflowStepSection>

            {/* Pièces du dossier — exonération (hors étapes, visible sans rien ouvrir) */}
            {client?.account_id && pdlList.some(estPdlExonere) && (
              <PiecesDossierExoneration
                accountId={client.account_id}
                fiscalYear={selectedYear}
                dossierCombine={!!dossierYear?.cerfa_bundle_generated_at}
              />
            )}

            {/* 6. Combinaison CERFAs */}
            <WorkflowStepSection
              stepIndex={5}
              title="Étape 6 : Combinaison des CERFAs"
              subtitle="Création d'un dossier PDF unique."
              icon={FiLayers}
              isActive={openStepIndex === 5}
              isOpen={openStepIndex === 5}
              onToggle={() => setOpenStepIndex(openStepIndex === 5 ? null : 5)}
            >
              {/* Cas A : Prêt à combiner */}
              {dossierYear?.admin_cerfa_generation_completed_at && !dossierYear?.cerfa_bundle_generated_at && (
                 <div className="p-3 bg-gray-50 rounded border border-gray-200">
                    <p className="text-xs text-slate-600 mb-3">Génération confirmée. Combinez les documents en un seul PDF.</p>
                    <button onClick={handleCombineCerfas} disabled={isCombiningCerfas} className={`w-full sm:w-auto px-4 py-2 text-sm font-medium rounded shadow-sm text-white flex items-center justify-center ${isCombiningCerfas ? 'bg-gray-400' : 'bg-cyan-600 hover:bg-cyan-700'}`}>
                      {isCombiningCerfas ? <FiLoader className="animate-spin h-4 w-4 mr-1.5" /> : <FiLayers className="h-4 w-4 mr-1.5" />}
                      Combiner les CERFA
                    </button>
                 </div>
              )}

              {/* Cas B : Déjà combiné */}
              {dossierYear?.cerfa_bundle_generated_at && (
                <div className="p-3 bg-green-50 border border-green-200 rounded flex justify-between items-center">
                  <div>
                    <p className="text-xs text-green-700 font-medium flex items-center">
                        <FiCheckSquare className="h-4 w-4 mr-1.5" />
                        Dossier CERFA combiné créé le {new Date(dossierYear.cerfa_bundle_generated_at).toLocaleDateString('fr-FR')}.
                    </p>
                    <p className="text-[10px] text-green-600 mt-1 ml-6">
                        Prêt pour l'envoi Yousign (Étape 7).
                    </p>
                  </div>

                  {/* BOUTON SUPPRIMER LE BUNDLE */}
                  {dossierYear?.cerfa_bundle_generated_at && (
                    <button
                      onClick={handleDeleteCerfaBundle}
                      className="p-2 text-red-500 hover:text-red-700 hover:bg-red-100 rounded-full transition-colors"
                      title="Supprimer le dossier combiné et recommencer"
                    >
                      <FiTrash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              )}
            </WorkflowStepSection>

            {/* 7. Signature Yousign */}
             <WorkflowStepSection
              stepIndex={6}
              title="Étape 7 : Signature Électronique"
              subtitle="Envoi du dossier via Yousign."
              icon={FiPenTool} // Nouvelle icône
              isActive={openStepIndex === 6}
              isOpen={openStepIndex === 6}
              onToggle={() => setOpenStepIndex(openStepIndex === 6 ? null : 6)}
            >
              <div className="space-y-4">
                  {/* Cas 1 : Pas encore envoyé */}
                  {dossierYear?.dossier_stage !== 'en_attente_signature' && dossierYear?.dossier_stage !== 'signe' && (
                      <div className="p-4 bg-white border rounded shadow-sm">
                          <p className="text-sm text-gray-600 mb-4">
                              Le dossier combiné est prêt. Cliquez ci-dessous pour créer une procédure de signature Yousign et l'envoyer automatiquement au client.
                          </p>
                          <button
                            onClick={handleSendYousignRequest}
                            disabled={isNotifying}
                            className="w-full sm:w-auto px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded hover:bg-indigo-700 flex items-center justify-center"
                          >
                             {isNotifying ? <FiLoader className="animate-spin mr-2"/> : <FiSend className="mr-2"/>}
                             Envoyer via Yousign
                          </button>
                      </div>
                  )}

                  {/* Cas 2 : En attente de signature */}
                  {dossierYear?.dossier_stage === 'en_attente_signature' && (
                      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded">
                          <div className="flex items-center justify-between">
                              {/* Partie Gauche : Texte */}
                              <div className="flex items-center">
                                  <FiClock className="h-5 w-5 text-yellow-600 mr-2"/>
                                  <div>
                                      <h4 className="text-sm font-bold text-yellow-800">En attente de signature</h4>
                                      <p className="text-xs text-yellow-700 mt-1">
                                          La demande a été envoyée via Yousign.
                                      </p>
                                  </div>
                              </div>
                              
                              {/* Partie Droite : Les Boutons */}
                              <div className="flex items-center space-x-2">
                                  {/* BOUTON SYNC (NOUVEAU) */}
                                  <button
                                    onClick={handleCheckStatus}
                                    className="px-3 py-1.5 bg-blue-50 border border-blue-200 text-blue-700 text-xs font-medium rounded hover:bg-blue-100 flex items-center transition-colors"
                                    title="Forcer la vérification du statut"
                                  >
                                    <FiRefreshCw className="mr-1.5 h-3 w-3"/>
                                    Vérifier
                                  </button>

                                  {/* BOUTON RELANCER (EXISTANT) */}
                                  <button
                                    onClick={handleRetrySignature}
                                    disabled={isNotifying}
                                    className="px-3 py-1.5 bg-white border border-yellow-300 text-yellow-800 text-xs font-medium rounded hover:bg-yellow-100 flex items-center transition-colors"
                                  >
                                    {isNotifying ? (
                                      <FiLoader className="animate-spin mr-1.5 h-3 w-3"/>
                                    ) : (
                                      <FiSend className="mr-1.5 h-3 w-3"/> // J'ai changé l'icône pour différencier
                                    )}
                                    Relancer
                                  </button>
                              </div>
                          </div>
                          
                          <div className="mt-3 ml-7">
                             <p className="text-xs text-slate-500">ID Demande: {dossierYear?.yousign_request_id}</p>
                          </div>
                      </div>
                  )}

                  {/* Cas 3 : Signé */}
                  {dossierYear?.dossier_stage === 'signe' && (
                      <div className="p-4 bg-green-50 border border-green-200 rounded">
                           <div className="flex items-center">
                              <FiCheckCircle className="h-5 w-5 text-green-600 mr-2"/>
                              <h4 className="text-sm font-bold text-green-800">Dossier Signé !</h4>
                          </div>
                          <p className="text-xs text-green-700 mt-1 ml-7">
                              Le dossier a été signé électroniquement le {dossierYear?.cerfa_signed_at ? new Date(dossierYear.cerfa_signed_at).toLocaleDateString() : 'N/A'}.
                          </p>
                          {!dossierYear?.signed_storage_path && (
                            <div className="mt-2 ml-7 flex items-center gap-2">
                              <p className="text-xs text-yellow-700">
                                <FiAlertTriangle className="inline h-3 w-3 mr-1" />
                                Fichier signé non encore disponible.
                              </p>
                              <button
                                onClick={handleCheckStatus}
                                className="px-2 py-1 bg-blue-50 border border-blue-200 text-blue-700 text-xs font-medium rounded hover:bg-blue-100 flex items-center"
                              >
                                <FiRefreshCw className="mr-1 h-3 w-3"/> Vérifier
                              </button>
                            </div>
                          )}
                      </div>
                  )}
              </div>
            </WorkflowStepSection>

            {/* 8. Documents Générés */}
              {generatedCerfas.length > 0 && (
                <SectionTitle 
                  title="Documents CERFA Générés" 
                  subtitle="Liste de tous les documents générés"
                  icon={FiFileText}
                  badge={generatedCerfas.length}
                >
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Type</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Fichier</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Exercice</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-slate-200">
                        {generatedCerfas.map((cerfa) => (
                          <tr key={cerfa.id}>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <div className="flex items-center space-x-2">
                                {cerfa.document_type === 'CERFA_BUNDLE' && (
                                  <FiLayers className="h-4 w-4 text-indigo-600" />
                                )}
                                <span className="text-xs">{cerfa.document_type}</span>
                                {cerfa.document_type === 'CERFA_BUNDLE' && (
                                  <span className="px-2 py-0.5 bg-purple-100 text-purple-800 text-xs font-medium rounded">
                                    Bundle
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-xs font-medium">
                              <button 
                                onClick={() => handleDownloadGeneratedCerfa(
                                  cerfa.storage_path, 
                                  cerfa.file_name,
                                  cerfa.document_type // ✅ AJOUT DU TYPE
                                )} 
                                className="underline text-slate-900 hover:text-blue-600"
                              >
                                {cerfa.file_name}
                              </button>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-xs">
                              {new Date(cerfa.exercice_end_date).toLocaleDateString('fr-FR')}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-right text-xs space-x-1">
                              <button
                                onClick={() => handlePreviewGeneratedCerfa(
                                  cerfa.storage_path,
                                  cerfa.document_type // ✅ AJOUT DU TYPE
                                )}
                                className="text-blue-600 hover:text-blue-800" 
                                title="Aperçu"
                              >
                                <FiEye />
                              </button>
                              <button
                                onClick={() => handleDeleteGeneratedCerfa(
                                  cerfa.id, 
                                  cerfa.storage_path,
                                  cerfa.document_type // ✅ AJOUT DU TYPE
                                )}
                                className="text-red-600 hover:text-red-800" 
                                title="Supprimer"
                              >
                                <FiXSquare />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </SectionTitle>
              )}

            {/* Documents Signés (avec fallback dossierYear.signed_storage_path) */}
            {(() => {
              // Construire la liste d'affichage : signed_cerfas d'abord, fallback dossierYear
              let signedDocsForUI = [...(signedCerfaDocuments || [])];
              if (signedDocsForUI.length === 0 && dossierYear?.signed_storage_path) {
                const sp = dossierYear.signed_storage_path;
                const fileName = sp.includes('/') ? sp.split('/').pop() : `DOSSIER_CERFA_SIGNE_${selectedYear}.pdf`;
                signedDocsForUI = [{
                  id: `dossierYear-${accountId}-${selectedYear}`,
                  storage_path: sp,
                  file_name: fileName,
                  signed_at: dossierYear.cerfa_signed_at || dossierYear.updated_at || null,
                }];
              }
              if (signedDocsForUI.length === 0) return null;
              return (
                <SectionTitle
                  title="Documents CERFA Signés"
                  subtitle="Documents validés et signés par le client"
                  icon={FiCheckSquare}
                  badge={signedDocsForUI.length}
                >
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200">
                      <tbody className="bg-white divide-y divide-slate-200">
                        {signedDocsForUI.map((docSigne) => (
                          <tr key={docSigne.id}>
                            <td className="px-3 py-2 whitespace-nowrap text-xs font-medium">
                                <button onClick={() => handleDownloadSignedCerfaBundle(docSigne.storage_path, docSigne.file_name)} className="underline text-slate-900 hover:text-blue-600">
                                    {docSigne.file_name}
                                </button>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-700">
                                {docSigne.signed_at ? new Date(docSigne.signed_at).toLocaleDateString('fr-FR') : 'En attente'}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-right text-xs space-x-1">
                              <button onClick={() => handlePreviewSignedCerfaBundle(docSigne.storage_path)} className="text-blue-600 hover:text-blue-800" title="Aperçu"><FiEye /></button>
                              <button onClick={() => handleDownloadSignedCerfaBundle(docSigne.storage_path, docSigne.file_name)} className="text-green-600 hover:text-green-800" title="Télécharger"><FiDownload /></button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </SectionTitle>
              );
            })()}

          </div>
        )}

        {activeTab === 'assimilation' && (
          <div className="h-full overflow-y-auto p-4">
            <AssimilationTabContent accountId={client.account_id} />
          </div>
        )}
      </div>
      {/* ✅ Modal édition TICFE (OCR + overrides + lignes manuelles) */}
      <TicfeInvoiceEditorModal
        open={isTicfeModalOpen}
        onClose={(saved) => {
          setIsTicfeModalOpen(false);
          setTicfeModalPdl(null);
          if (saved) {
            toast.success("Champs enregistrés. Vous pouvez regénérer l’ERA.");
            fetchManualLinesCounts(); // Rafraîchir les compteurs de lignes manuelles
          }
        }}
        accountId={client?.account_id}
        pdl={ticfeModalPdl}
        year={selectedYear}
        adminUserId={session?.user?.id || null}
      />

      {/* Modale SIRET établissement + code APE par PDL */}
      <PdlSiretEditorModal
        open={isSiretModalOpen}
        onClose={() => {
          setIsSiretModalOpen(false);
          setSiretModalPdl(null);
        }}
        pdl={siretModalPdl}
        client={client}
        table="pdl"
        onSaved={handlePdlSaved}
      />

      {/* Confirmation d'une régénération d'ERA sur exercice verrouillé (option C, 03/11/2026) */}
      <RegenerationVerrouilleeModal
        open={!!verrouEra}
        pdl={verrouEra?.pdl}
        message={verrouEra?.message}
        onClose={() => setVerrouEra(null)}
        onConfirm={(motif) => {
          const { pdl } = verrouEra;
          setVerrouEra(null);
          handleGenerateCerfaERA(pdl, { forcer: true, motif });
        }}
      />

      {/* Fenêtre d'exonération d'accise par PDL (E04, art. L312-31 CIBS) */}
      <PdlExonerationEditorModal
        open={isExoModalOpen}
        onClose={() => {
          setIsExoModalOpen(false);
          setExoModalPdl(null);
        }}
        pdl={exoModalPdl}
        onSaved={handlePdlSaved}
      />

      {/* Modale édition raison sociale (accounts.company_name) */}
      <AccountRaisonSocialeEditorModal
        open={isRaisonSocialeModalOpen}
        onClose={() => setIsRaisonSocialeModalOpen(false)}
        client={client}
        onSaved={(updated) => setClient((prev) => ({ ...prev, ...updated }))}
      />

      {/* Modale d'aperçu compacte */}
      {isPreviewOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded shadow-xl max-w-4xl w-full h-[85vh] flex flex-col m-4">
            <div className="flex justify-between items-center p-3 border-b">
              <h2 className="text-sm font-medium">Aperçu du PDF</h2>
              <button onClick={() => { setIsPreviewOpen(false); setPreviewUrl(null); }} className="text-gray-500 hover:text-gray-700">
                <FiX className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1">
              {previewUrl ? (
                <iframe src={previewUrl} title="Aperçu PDF" className="w-full h-full border-0" />
              ) : (
                <p className="text-center p-6 text-sm">Chargement...</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyseCspe;
